import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { APP_NAME } from '../config/frontend';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import POSView1 from '../components/POSView1';
import POSView2 from '../components/POSView2';
import POSReceipt from '../components/POSReceipt';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  ChevronDown,
  ChevronUp,
  Activity,
  PlusCircle,
  Plus,
  Receipt,
  RefreshCw,
  Filter,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Download,
  Folder,
  Calculator,
  Globe,
  Monitor,
  Barcode,
  ShoppingBag,
  RotateCcw,
  LogOut
} from 'lucide-react';
import toast from 'react-hot-toast';

const RetailPOS = () => {
  // Remove the debug log that's causing infinite re-renders
  
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { theme } = useContext(ThemeContext);
  const { formatCurrency, getSetting, settings } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [posStats, setPosStats] = useState({
    todaySales: 0,
    todayTransactions: 0,
    averageTicket: 0,
    topProduct: 'N/A',
    pendingReturns: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState({
    online: false,
    active: false,
    lastCheck: null
  });
  const [isIdle, setIsIdle] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);
  const [isUomFocused, setIsUomFocused] = useState(false);
  const [uomValue, setUomValue] = useState('Ctn');
  const [posView, setPosView] = useState('view1');
  const [cartItems, setCartItems] = useState(() => {
    // Load cart items from localStorage on component mount
    const savedCart = localStorage.getItem('posCartItems');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [paidAmount, setPaidAmount] = useState(0);
  const [isProcessingSale, setIsProcessingSale] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [heldSales, setHeldSales] = useState(() => {
    // Load held sales from localStorage on component mount
    const savedHeldSales = localStorage.getItem('heldSales');
    return savedHeldSales ? JSON.parse(savedHeldSales) : [];
  });
  const [showHeldSalesModal, setShowHeldSalesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [showReturnSaleModal, setShowReturnSaleModal] = useState(false);
  const [returnSaleSearchTerm, setReturnSaleSearchTerm] = useState('');
  const [returnSaleResults, setReturnSaleResults] = useState([]);
  const [selectedReturnSale, setSelectedReturnSale] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [isProcessingReturn, setIsProcessingReturn] = useState(false);
  const [transactionCount, setTransactionCount] = useState(0);
  
  // Sales History State
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(false);
  const [salesHistorySearchTerm, setSalesHistorySearchTerm] = useState('');
  const [salesHistoryFilter, setSalesHistoryFilter] = useState('all'); // all, today, week, month
  const [salesHistoryStartDate, setSalesHistoryStartDate] = useState('');
  const [salesHistoryEndDate, setSalesHistoryEndDate] = useState('');
  const [salesHistoryStatus, setSalesHistoryStatus] = useState('all');
  const [salesHistoryCurrentPage, setSalesHistoryCurrentPage] = useState(1);
  const [salesHistoryPerPage, setSalesHistoryPerPage] = useState(20);
  const [salesHistoryTotal, setSalesHistoryTotal] = useState(0);
  const [salesHistorySortBy, setSalesHistorySortBy] = useState('created_at');
  const [salesHistorySortOrder, setSalesHistorySortOrder] = useState('desc');
  
  // Work Period Management
  const [workPeriod, setWorkPeriod] = useState({
    isActive: false,
    startTime: null,
    endTime: null,
    startDate: null,
    endDate: null,
    startedBy: null,
    endedBy: null
  });
  const [showWorkPeriodModal, setShowWorkPeriodModal] = useState(false);
  const [workPeriodHistory, setWorkPeriodHistory] = useState([]);
  const [workPeriodLoading, setWorkPeriodLoading] = useState(false);

  // POS Reports States
  const [reportsData, setReportsData] = useState({
    dashboardStats: null,
    salesReport: null,
    productReport: null,
    paymentMethodReport: null
  });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReportTab, setActiveReportTab] = useState('dashboard');
  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleDetails, setShowSaleDetails] = useState(false);
  
  // Receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptSettings, setReceiptSettings] = useState({
    company_name: 'Grovalue Enterprise',
    company_address: '123 Business Street, City, Country',
    company_phone: '+233 XX XXX XXXX',
    company_email: '',
    company_website: '',
    tax_number: '',
    currency_symbol: 'Ghs',
    receipt_footer: `Thank you for your business!\nKeep this receipt for your records\nPowered by ${APP_NAME} System`
  });

  // Handle receipt actions
  const handleReceiptPrint = (receiptElement) => {
    if (receiptElement) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>POS Receipt - ${receiptData.reference_number}</title>
            <style>
              @page {
                size: 80mm 200mm;
                margin: 0;
              }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 10px;
                background: white;
                width: 80mm;
                line-height: 1.2;
              }
              .receipt { 
                width: 100%; 
                margin: 0 auto; 
                padding: 0;
              }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              .border-t { border-top: 1px solid #000; }
              .border-b { border-bottom: 1px solid #000; }
              .mb-1 { margin-bottom: 2px; }
              .mb-2 { margin-bottom: 4px; }
              .mb-3 { margin-bottom: 6px; }
              .mb-4 { margin-bottom: 8px; }
              .mb-6 { margin-bottom: 12px; }
              .mt-1 { margin-top: 2px; }
              .mt-2 { margin-top: 4px; }
              .mt-3 { margin-top: 6px; }
              .mt-6 { margin-top: 12px; }
              .pt-2 { padding-top: 4px; }
              .pt-3 { padding-top: 6px; }
              .pt-4 { padding-top: 8px; }
              .py-3 { padding-top: 6px; padding-bottom: 6px; }
              .flex { display: flex; }
              .justify-between { justify-content: space-between; }
              .items-start { align-items: flex-start; }
              .w-1-2 { width: 50%; }
              .w-1-6 { width: 16.666667%; }
              .pr-2 { padding-right: 4px; }
              .text-xs { font-size: 10px; }
              .text-sm { font-size: 12px; }
              .text-lg { font-size: 14px; }
              .text-xl { font-size: 16px; }
              .leading-tight { line-height: 1.1; }
              .tracking-wider { letter-spacing: 0.05em; }
              .text-gray-600 { color: #4b5563; }
              .text-gray-700 { color: #374151; }
              .text-gray-500 { color: #6b7280; }
              .text-gray-400 { color: #9ca3af; }
              .text-red-600 { color: #dc2626; }
              .text-green-600 { color: #16a34a; }
              @media print {
                body { margin: 0; padding: 5px; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptElement.innerHTML}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleReceiptDownload = (receiptElement) => {
    if (receiptElement) {
      const content = receiptElement.innerText;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${receiptData.reference_number}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Handle print receipt from sales history - direct print
  const handlePrintReceiptFromHistory = () => {
    if (!selectedSale) return;

    // Prepare receipt data from selected sale
    const totalAmount = selectedSale.items ? 
      selectedSale.items.reduce((total, item) => {
        const quantity = item.netQuantity !== undefined ? item.netQuantity : item.quantity;
        return total + (parseFloat(item.price || 0) * quantity);
      }, 0) : 
      parseFloat(selectedSale.total_amount || 0);

    // Create receipt HTML content
    const receiptContent = `
      <div style="font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.4; max-width: 300px; margin: 0 auto; padding: 10px;">
        <!-- Receipt Header -->
        <div style="text-align: center; margin-bottom: 20px;">
          <div style="font-size: 18px; font-weight: bold; margin-bottom: 8px; letter-spacing: 2px;">${APP_NAME} SYSTEM</div>
          <div style="font-size: 10px; color: #666; margin-bottom: 8px;">Point of Sale Receipt</div>
          <div style="font-size: 10px; font-weight: bold;">${receiptSettings.company_name}</div>
          <div style="font-size: 10px; color: #666;">${receiptSettings.company_address}</div>
          <div style="font-size: 10px; color: #666;">Tel: ${receiptSettings.company_phone}</div>
          ${receiptSettings.company_email ? `<div style="font-size: 10px; color: #666;">Email: ${receiptSettings.company_email}</div>` : ''}
          ${receiptSettings.company_website ? `<div style="font-size: 10px; color: #666;">Web: ${receiptSettings.company_website}</div>` : ''}
          ${receiptSettings.tax_number ? `<div style="font-size: 10px; color: #666;">Tax ID: ${receiptSettings.tax_number}</div>` : ''}
          <div style="border-top: 1px solid #000; margin: 12px 0;"></div>
        </div>

        <!-- Receipt Details -->
        <div style="margin-bottom: 16px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
            <span>Receipt #:</span>
            <span style="font-weight: bold;">${selectedSale.reference_number}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
            <span>Date:</span>
            <span>${new Date(selectedSale.created_at).toLocaleDateString('en-GB')}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
            <span>Time:</span>
            <span>${new Date(selectedSale.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
            <span>Cashier:</span>
            <span>${selectedSale.cashier_name || user?.name || user?.username || 'Unknown'}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
            <span>Payment:</span>
            <span>${selectedSale.payment_method}</span>
          </div>
          <div style="border-top: 1px solid #000;"></div>
        </div>

        <!-- Items -->
        <div style="margin-bottom: 16px;">
          <div style="margin-bottom: 12px;">
            <div style="display: flex; justify-content: space-between; font-size: 10px; font-weight: bold;">
              <span style="width: 50%;">ITEM</span>
              <span style="width: 16.67%; text-align: center;">QTY</span>
              <span style="width: 16.67%; text-align: center;">UOM</span>
              <span style="width: 16.67%; text-align: right;">AMOUNT</span>
            </div>
            <div style="border-top: 1px solid #000; margin-top: 4px;"></div>
          </div>
          
          ${selectedSale.items ? selectedSale.items.map(item => {
            const quantity = item.netQuantity !== undefined ? item.netQuantity : item.quantity;
            const amount = parseFloat(item.price || 0) * quantity;
            return `
              <div style="margin-bottom: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                  <div style="width: 50%; padding-right: 8px;">
                    <div style="font-weight: bold; font-size: 10px; line-height: 1.2;">${item.product?.name || 'Unknown Product'}</div>
                    <div style="font-size: 10px; color: #666;">
                      @ ${receiptSettings.currency_symbol} ${parseFloat(item.price || 0).toFixed(2)}/${item.unit_of_measure || 'PCS'}
                    </div>
                  </div>
                  <div style="width: 16.67%; text-align: center; font-size: 10px;">${quantity}</div>
                  <div style="width: 16.67%; text-align: center; font-size: 10px;">${item.unit_of_measure || 'PCS'}</div>
                  <div style="width: 16.67%; text-align: right; font-size: 10px; font-weight: bold;">
                    ${receiptSettings.currency_symbol} ${amount.toFixed(2)}
                  </div>
                </div>
              </div>
            `;
          }).join('') : ''}
        </div>

        <!-- Totals -->
        <div style="border-top: 1px solid #000; padding-top: 12px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
            <span>Subtotal:</span>
            <span>${receiptSettings.currency_symbol} ${totalAmount.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px; color: #dc2626;">
            <span>Discount:</span>
            <span>-${receiptSettings.currency_symbol} 0.00</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
            <span>VAT (0%):</span>
            <span>${receiptSettings.currency_symbol} 0.00</span>
          </div>
          <div style="border-top: 1px solid #000; padding-top: 8px; margin-top: 8px;">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 12px;">
              <span>TOTAL:</span>
              <span>${receiptSettings.currency_symbol} ${totalAmount.toFixed(2)}</span>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 8px; font-size: 10px;">
            <span>Amount Paid:</span>
            <span>${receiptSettings.currency_symbol} ${parseFloat(selectedSale.amount_paid || totalAmount).toFixed(2)}</span>
          </div>
          ${parseFloat(selectedSale.change_amount || 0) > 0 ? `
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 10px; color: #16a34a;">
              <span>Change:</span>
              <span>${receiptSettings.currency_symbol} ${parseFloat(selectedSale.change_amount || 0).toFixed(2)}</span>
            </div>
          ` : ''}
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #000;">
          ${receiptSettings.receipt_footer.split('\\n').map(line => 
            `<div style="font-size: 10px; color: #666; margin-bottom: 4px;">${line}</div>`
          ).join('')}
        </div>
      </div>
    `;

    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '80mm';
    iframe.style.height = '200mm';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>POS Receipt - ${selectedSale.reference_number}</title>
          <style>
            @page {
              size: 80mm 200mm;
              margin: 0;
            }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px; 
              margin: 0; 
              padding: 10px;
              background: white;
              width: 80mm;
              line-height: 1.2;
            }
            .receipt { 
              width: 100%; 
              margin: 0 auto; 
              padding: 0;
            }
            @media print {
              body { margin: 0; padding: 5px; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${receiptContent}</div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show success message
    toast.success('Receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  // Fetch receipt settings
  const fetchReceiptSettings = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/settings/receipt/info`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReceiptSettings(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching receipt settings:', error);
    }
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch receipt settings on component mount
  useEffect(() => {
    fetchReceiptSettings();
  }, [fetchReceiptSettings]);

  // Fetch transaction count on component mount and refresh periodically
  useEffect(() => {
    fetchTransactionCount();
    
    // Refresh transaction count every 30 seconds to keep it current
    const interval = setInterval(() => {
      fetchTransactionCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch work period status on mount
  useEffect(() => {
    fetchWorkPeriodStatus();
  }, []);

  // Fetch reports when POS Reports modal opens
  useEffect(() => {
    if (activeModal === 'pos-reports') {
      fetchAllReports();
    }
  }, [activeModal]);

  // Debug modal state
  useEffect(() => {
    if (modalOpen) {
      console.log('Modal is open, footer should be visible');
    }
  }, [modalOpen]);

  // Load POS view type from settings and update when settings change
  useEffect(() => {
    const viewSetting = getSetting('pos_view_type', 1);
    const newView = viewSetting === 1 ? 'view1' : 'view2';
    setPosView(newView);
    console.log('POS view updated to:', newView);
    console.log('Current posView state:', posView);
  }, [getSetting, settings]);

  // Manual system status check function
  const checkSystemStatus = async () => {
    try {
      // Check if user is authenticated
      const isAuthenticated = !!user;
      
      // Check if backend is responding using the actual API endpoint
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/products/search?q=test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        // Add a timeout to prevent hanging
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      
      const isBackendOnline = response.ok;
      console.log('Backend status check:', isBackendOnline ? 'ONLINE' : 'OFFLINE');
      
      setSystemStatus({
        online: isAuthenticated && isBackendOnline,
        active: isAuthenticated && !isIdle,
        lastCheck: new Date()
      });
    } catch (error) {
      console.log('System status check failed - Backend OFFLINE:', error);
      setSystemStatus({
        online: false, // Backend is offline
        active: false,
        lastCheck: new Date()
      });
    }
  };

  // Check system status periodically
  useEffect(() => {
    checkSystemStatus();
    const interval = setInterval(checkSystemStatus, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [user, isIdle]);

  // Global F2, F3, F4 key binding
  useEffect(() => {
    const handleKeyDown = (e) => {
      console.log('Key pressed:', e.key, 'Modal open:', modalOpen, 'Active modal:', activeModal);
      if (e.key === 'F2') {
        e.preventDefault();
        console.log('F2 key detected, calling handleF2Key');
        handleF2Key();
      } else if (e.key === 'F3') {
        e.preventDefault();
        console.log('F3 key detected, calling handleF3Key');
        handleF3Key();
      } else if (e.key === 'F4') {
        e.preventDefault();
        console.log('F4 key detected, calling handleF4Key');
        handleF4Key();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [cartItems, paymentMethod, modalOpen, activeModal]); // Re-run when dependencies change

  // Add idle detection
  useEffect(() => {
    let idleTimer;
    
    const resetIdleTimer = () => {
      setIsIdle(false);
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setIsIdle(true), 300000); // 5 minutes
    };
    
    // Reset timer on user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetIdleTimer));
    
    resetIdleTimer();
    
    return () => {
      events.forEach(event => document.removeEventListener(event, resetIdleTimer));
      clearTimeout(idleTimer);
    };
  }, []);

  // Search products from API
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      console.log('Searching for query:', query);
      
      // Try the Laravel API endpoint first
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/products/search?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Handle the search response format
        const products = data.products || data || [];
        const validProducts = Array.isArray(products) ? products.filter(p => p && p.name) : [];
        console.log('Valid products:', validProducts);
        setSearchResults(validProducts);
      } else {
        console.error('Search failed:', response.status);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Search sales for return function
  const searchSalesForReturn = async (query) => {
    if (!query || query.length < 2) {
      setReturnSaleResults([]);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReturnSaleResults(data.sales || data || []);
      } else {
        setReturnSaleResults([]);
      }
    } catch (error) {
      console.error('Search sales error:', error);
      setReturnSaleResults([]);
    }
  };

  // Fetch transaction count from database
  const fetchTransactionCount = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales/count`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTransactionCount(data.count || 0);
      } else {
        console.error('Failed to fetch transaction count');
        setTransactionCount(0);
      }
    } catch (error) {
      console.error('Error fetching transaction count:', error);
      setTransactionCount(0);
    }
  };

  // Fetch sales history
  const fetchSalesHistory = async (searchTerm = '', filter = 'all') => {
    setSalesHistoryLoading(true);
    try {
      let url = `${import.meta.env.VITE_API_BASE_URL}/sales?`;
      
      if (searchTerm) {
        url += `search=${encodeURIComponent(searchTerm)}&`;
      }
      
      if (filter !== 'all') {
        url += `filter=${filter}&`;
      }
      
      // Add date range filters
      if (salesHistoryStartDate) {
        url += `start_date=${encodeURIComponent(salesHistoryStartDate)}&`;
      }
      if (salesHistoryEndDate) {
        url += `end_date=${encodeURIComponent(salesHistoryEndDate)}&`;
      }
      if (salesHistoryStatus && salesHistoryStatus !== 'all') {
        url += `status=${encodeURIComponent(salesHistoryStatus)}&`;
      }
      
      // Add pagination parameters
      url += `page=${salesHistoryCurrentPage}&per_page=${salesHistoryPerPage}`;
      
      // Add sorting parameters
      url += `&sort_by=${salesHistorySortBy}&sort_order=${salesHistorySortOrder}`;
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const sales = data.sales || [];
        
        // Calculate net quantities for each sale
        const salesWithNetQuantities = await Promise.all(
          sales.map(async (sale) => {
            try {
              // Fetch returns for this sale
              const returnResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales?original_sale_id=${sale.id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                  'Content-Type': 'application/json',
                },
              });

              if (returnResponse.ok) {
                const returnData = await returnResponse.json();
                const returns = returnData.sales || [];

                // Calculate net quantities for each item
                const itemsWithNetQuantities = sale.items?.map(item => {
                  // Find returns for this specific product
                  const productReturns = returns.filter(returnSale => 
                    returnSale.items && returnSale.items.some(returnItem => returnItem.product_id === item.product_id)
                  );

                  // Calculate total returned quantity for this product
                  let totalReturnedQuantity = 0;
                  productReturns.forEach(returnSale => {
                    const returnItem = returnSale.items.find(returnItem => returnItem.product_id === item.product_id);
                    if (returnItem) {
                      totalReturnedQuantity += parseFloat(returnItem.quantity);
                    }
                  });

                  // Calculate net quantity
                  const netQuantity = parseFloat(item.quantity) - totalReturnedQuantity;

                  return {
                    ...item,
                    originalQuantity: parseFloat(item.quantity),
                    returnedQuantity: totalReturnedQuantity,
                    netQuantity: Math.max(0, netQuantity)
                  };
                }) || [];

                // Calculate net total amount
                const netTotalAmount = itemsWithNetQuantities.reduce((total, item) => {
                  const quantity = item.netQuantity !== undefined ? item.netQuantity : item.quantity;
                  return total + (parseFloat(item.price || 0) * quantity);
                }, 0);

                return {
                  ...sale,
                  items: itemsWithNetQuantities,
                  netTotalAmount: netTotalAmount
                };
              }
            } catch (error) {
              console.error('Error calculating net quantities for sale:', sale.id, error);
            }

            // Return original sale if calculation fails
            return {
              ...sale,
              netTotalAmount: parseFloat(sale.total_amount || 0)
            };
          })
        );
        
        setSalesHistory(salesWithNetQuantities);
        setSalesHistoryTotal(data.total || 0);
      } else {
        console.error('Error fetching sales history:', response.statusText);
        toast.error('Failed to load sales history', {
          duration: 3000,
          icon: '⚠️',
          style: {
            background: '#EF4444',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
      }
    } catch (error) {
      console.error('Error fetching sales history:', error);
      toast.error('Network error while loading sales history', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    } finally {
      setSalesHistoryLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    console.log('Search effect triggered:', { searchTerm, isQuantityFocused, isUomFocused });
    
    const timeoutId = setTimeout(() => {
      // Only search if we don't have a selected product or if the search term is different from the selected product name
      const shouldSearch = searchTerm && 
                          searchTerm.length >= 2 && 
                          !isQuantityFocused && 
                          !isUomFocused && 
                          (!selectedProduct || searchTerm !== selectedProduct.name);
      
      if (shouldSearch) {
        console.log('Searching for:', searchTerm);
        searchProducts(searchTerm);
        setSelectedIndex(-1); // Reset selection when search changes
      } else if (!isQuantityFocused && !isUomFocused) {
        setSearchResults([]);
        setSelectedIndex(-1);
        
        // Reset values when search field is empty
        if (!searchTerm || searchTerm.length === 0) {
          console.log('Resetting values - search field empty');
          setSelectedProduct(null);
          setQuantity(1);
          setUomValue('Ctn');
        }
      }
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isQuantityFocused, isUomFocused, selectedProduct]);

  // Clear search results when focus moves away from search field
  useEffect(() => {
    const handleFocusOut = (e) => {
      if (!e.target.closest('.search-container')) {
        setSearchResults([]);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, []);

  // Add item to cart
  const addToCart = () => {
    if (!selectedProduct) return;
    
    const price = uomValue === 'Ctn' ? selectedProduct.selling_price_ctn : selectedProduct.selling_price_pcs;
    const amount = price * quantity;
    
    setCartItems(prev => {
      // Check if item with same product and UoM already exists
      const existingItemIndex = prev.findIndex(item => 
        item.product.id === selectedProduct.id && item.uom === uomValue
      );
      
      if (existingItemIndex !== -1) {
        // Item exists, update quantity and amount, then move to top
        const updatedItems = [...prev];
        const existingItem = updatedItems[existingItemIndex];
        const newQuantity = existingItem.quantity + quantity;
        const newAmount = existingItem.price * newQuantity;
        
        // Remove the existing item
        updatedItems.splice(existingItemIndex, 1);
        
        // Add updated item at the top
        const updatedItem = {
          ...existingItem,
          quantity: newQuantity,
          amount: newAmount
        };
        
        console.log('Item quantity updated in cart and moved to top:', updatedItem);
        return [updatedItem, ...updatedItems];
      } else {
        // Item doesn't exist, add new item at the top
        const cartItem = {
          id: Date.now(), // Unique ID for the cart item
          product: selectedProduct,
          quantity: quantity,
          uom: uomValue,
          price: price,
          amount: amount
        };
        
        console.log('New item added to cart at top:', cartItem);
        return [cartItem, ...prev];
      }
    });
    
    // Reset form after adding to cart
    setSelectedProduct(null);
    setSearchTerm('');
    setQuantity(1);
    setUomValue('Ctn');
    setSearchResults([]);
    setSelectedIndex(-1);
    
    // Focus back to search field for next item
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
      if (searchInput) {
        searchInput.focus();
        console.log('Focus returned to search field');
      }
    }, 100);
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    try {
      console.log('Selecting product:', product);
      
      // Validate product data
      if (!product || !product.name) {
        console.error('Invalid product data:', product);
        return;
      }
      
      // Ensure price values are properly formatted
      const sanitizedProduct = {
        ...product,
        selling_price_pcs: parseFloat(product.selling_price_pcs) || 0,
        selling_price_ctn: parseFloat(product.selling_price_ctn) || 0,
        quantity: parseFloat(product.quantity) || 0
      };
      
      console.log('Sanitized product:', sanitizedProduct);
      
      // Check if product is out of stock
      if (sanitizedProduct.quantity <= 0) {
        // Show toast notification for out of stock
        toast.error(`${sanitizedProduct.name} is out of stock!`, {
          duration: 3000,
          icon: '⚠️',
          style: {
            background: '#EF4444',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });
        
        // Clear search field and reset
        setSearchTerm('');
        setSearchResults([]);
        setSelectedIndex(-1);
        setSelectedProduct(null);
        
        // Focus back to search field for next search
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
          if (searchInput) {
            searchInput.focus();
            console.log('Focus returned to search field after out-of-stock notification');
          }
        }, 100);
        
        return; // Don't proceed with selection
      }
      
      setSelectedProduct(sanitizedProduct);
      setSearchTerm(product.name);
      setSearchResults([]);
      setSelectedIndex(-1);
      
      // Move focus to quantity field after a short delay
      setTimeout(() => {
        const quantityInput = document.getElementById('quantity-input');
        if (quantityInput) {
          quantityInput.focus();
          quantityInput.select(); // Select the current value for easy replacement
        }
      }, 100);
    } catch (error) {
      console.error('Error in handleProductSelect:', error);
    }
  };

  // Handle search field focus
  const handleSearchFocus = () => {
    if (searchTerm && searchTerm.length >= 2 && searchResults.length > 0 && !isQuantityFocused && !isUomFocused) {
      setSearchResults(searchResults); // Re-show dropdown if there are results and neither quantity nor UoM focused
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < searchResults.length - 1 ? prev + 1 : 0;
          // Auto-scroll to keep selected item visible
          setTimeout(() => scrollToSelectedItem(newIndex), 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : searchResults.length - 1;
          // Auto-scroll to keep selected item visible
          setTimeout(() => scrollToSelectedItem(newIndex), 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleProductSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setSearchResults([]);
        setSelectedIndex(-1);
        break;
    }
  };

  // Helper function to save held sales to localStorage
  const saveHeldSalesToStorage = (heldSalesArray) => {
    localStorage.setItem('heldSales', JSON.stringify(heldSalesArray));
  };

  // Save modal state to localStorage
  const saveModalState = (isOpen, modalType) => {
    const modalState = {
      isOpen,
      activeModal: modalType,
      timestamp: new Date().toISOString()
    };
    console.log('🔧 Saving modal state:', modalState);
    localStorage.setItem('posModalState', JSON.stringify(modalState));
  };

  // Clear modal state from localStorage
  const clearModalState = () => {
    console.log('🔧 Clearing modal state from localStorage');
    localStorage.removeItem('posModalState');
  };

  // Save cart items to localStorage
  const saveCartToStorage = (cartArray) => {
    localStorage.setItem('posCartItems', JSON.stringify(cartArray));
  };

  // Clear cart from localStorage
  const clearCartFromStorage = () => {
    localStorage.removeItem('posCartItems');
  };

  // Show confirmation modal
  const showConfirmation = (action, data, title, message) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (confirmAction === 'cancel-cart') {
      handleCancelConfirmed();
    } else if (confirmAction === 'delete-held-sale') {
      handleDeleteHeldSaleConfirmed(confirmData);
    } else if (confirmAction === 'delete-cart-item') {
      handleDeleteCartItemConfirmed(confirmData);
    } else if (confirmAction === 'exit-pos') {
      handleExitPOSConfirmed();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  // Handle cancel confirmation
  const handleCancelConfirmed = () => {
    // Clear cart
    setCartItems([]);
    clearCartFromStorage(); // Clear from localStorage
    
    // Reset form fields
    setPaidAmount(0);
    setPaymentMethod('Cash');
    setSelectedProduct(null);
    setSearchTerm('');
    setQuantity(1);
    setUomValue('Ctn');
    setSearchResults([]);
    setSelectedIndex(-1);
    
    // Focus back to search field
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
    
    // Show success message
    toast.success('Cart cleared successfully!', {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });
  };

  // Handle delete held sale confirmation
  const handleDeleteHeldSaleConfirmed = (heldSaleId) => {
    setHeldSales(prev => {
      const updatedHeldSales = prev.filter(sale => sale.id !== heldSaleId);
      saveHeldSalesToStorage(updatedHeldSales);
      return updatedHeldSales;
    });
    
    toast.success('Held sale deleted successfully!', {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });
  };

  // Handle delete cart item confirmation
  const handleDeleteCartItemConfirmed = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
    
    toast.success('Item removed from cart!', {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });
  };

  // Handle exit POS confirmation
  const handleExitPOSConfirmed = () => {
    handleModalClose();
    clearModalState(); // Ensure modal state is cleared
    
    toast.success('Exited POS successfully!', {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });
  };

  // Handle sales history modal open
  const handleSalesHistoryOpen = () => {
    handleModalOpen('sales-history');
    fetchSalesHistory(); // Load initial sales data
  };

  // Handle sales history search
  const handleSalesHistorySearch = () => {
    fetchSalesHistory(salesHistorySearchTerm, salesHistoryFilter);
  };

  // Handle sales history filter change
  const handleSalesHistoryFilterChange = (filter) => {
    setSalesHistoryFilter(filter);
    fetchSalesHistory(salesHistorySearchTerm, filter);
  };

  // Handle apply sales history filter
  const handleApplySalesHistoryFilter = () => {
    fetchSalesHistory();
  };

  // Handle today filter
  const handleTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setSalesHistoryStartDate(today);
    setSalesHistoryEndDate(today);
    setSalesHistoryStatus('all');
    setSalesHistoryCurrentPage(1);
    fetchSalesHistory();
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (salesHistoryCurrentPage > 1) {
      setSalesHistoryCurrentPage(salesHistoryCurrentPage - 1);
      fetchSalesHistory();
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(salesHistoryTotal / salesHistoryPerPage);
    if (salesHistoryCurrentPage < totalPages) {
      setSalesHistoryCurrentPage(salesHistoryCurrentPage + 1);
      fetchSalesHistory();
    }
  };

  const handleSort = (column) => {
    if (salesHistorySortBy === column) {
      // Toggle sort order if same column
      setSalesHistorySortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSalesHistorySortBy(column);
      setSalesHistorySortOrder('asc');
    }
    setSalesHistoryCurrentPage(1); // Reset to first page
    fetchSalesHistory();
  };

  // POS Reports Functions
  const fetchDashboardStats = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/dashboard-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReportsData(prev => ({ ...prev, dashboardStats: data }));
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchSalesReport = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate,
        group_by: 'daily'
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/sales?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReportsData(prev => ({ ...prev, salesReport: data }));
        }
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
    }
  };

  const fetchProductReport = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate,
        limit: '10'
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/products?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReportsData(prev => ({ ...prev, productReport: data }));
        }
      }
    } catch (error) {
      console.error('Error fetching product report:', error);
    }
  };

  const fetchPaymentMethodReport = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/payment-methods?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReportsData(prev => ({ ...prev, paymentMethodReport: data }));
        }
      }
    } catch (error) {
      console.error('Error fetching payment method report:', error);
    }
  };

  const fetchAllReports = async () => {
    setReportsLoading(true);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchSalesReport(),
        fetchProductReport(),
        fetchPaymentMethodReport()
      ]);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleReportsDateChange = (field, value) => {
    setReportsDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyReportsFilter = () => {
    fetchAllReports();
  };

  const handleTodayReportsFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setReportsDateRange({ startDate: today, endDate: today });
    setTimeout(() => fetchAllReports(), 100);
  };

  // Work Period Management Functions
  const fetchWorkPeriodStatus = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/work-periods/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.work_period) {
          setWorkPeriod({
            isActive: true,
            startTime: data.work_period.start_time,
            endTime: data.work_period.end_time,
            startDate: data.work_period.work_date,
            endDate: data.work_period.end_time ? data.work_period.work_date : null,
            startedBy: data.work_period.user?.name || data.work_period.user?.username || 'Unknown',
            endedBy: null
          });
        } else {
          setWorkPeriod({
            isActive: false,
            startTime: null,
            endTime: null,
            startDate: null,
            endDate: null,
            startedBy: null,
            endedBy: null
          });
        }
      }
    } catch (error) {
      console.error('Error fetching work period status:', error);
    }
  };

  const fetchWorkPeriodHistory = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setWorkPeriodLoading(false);
      return;
    }

    setWorkPeriodLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/work-periods/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWorkPeriodHistory(data.work_periods.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching work period history:', error);
    } finally {
      setWorkPeriodLoading(false);
    }
  };

  const startWorkPeriod = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Please log in to manage work periods', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/work-periods/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: 'Work period started from POS'
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          await fetchWorkPeriodStatus();
          setShowWorkPeriodModal(false);
          toast.success('Work period started successfully!', {
            duration: 3000,
            icon: '🟢',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
        } else {
          toast.error(data.message || 'Failed to start work period', {
            duration: 3000,
            icon: '⚠️',
            style: {
              background: '#EF4444',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
        }
      }
    } catch (error) {
      console.error('Error starting work period:', error);
      toast.error('Failed to start work period', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    }
  };

  const endWorkPeriod = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Please log in to manage work periods', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/work-periods/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: 'Work period ended from POS'
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          await fetchWorkPeriodStatus();
          await fetchWorkPeriodHistory();
          setShowWorkPeriodModal(false);
          toast.success('Work period ended successfully!', {
            duration: 3000,
            icon: '🔴',
            style: {
              background: '#10B981',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
        } else {
          toast.error(data.message || 'Failed to end work period', {
            duration: 3000,
            icon: '⚠️',
            style: {
              background: '#EF4444',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
        }
      }
    } catch (error) {
      console.error('Error ending work period:', error);
      toast.error('Failed to end work period', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    }
  };

  const handleModalOpen = (modalType) => {
    if (modalType === 'new-sale' && !workPeriod.isActive) {
      toast.error('Cannot open POS. Please start a work period first!', {
        duration: 4000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }
    
    setActiveModal(modalType);
    setModalOpen(true);
    saveModalState(modalType);
  };

  // Calculate net quantities after returns
  const calculateNetQuantities = async (sale) => {
    console.log('Calculating net quantities for sale:', sale.id, sale.reference_number);
    try {
      // Fetch returns for this sale
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales?original_sale_id=${sale.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const returns = data.sales || []; // Changed from data.data to data.sales
        console.log('Returns found for sale:', sale.id, returns);

        // Calculate net quantities for each item
        const itemsWithNetQuantities = sale.items.map(item => {
          // Find returns for this specific product
          const productReturns = returns.filter(returnSale => 
            returnSale.items && returnSale.items.some(returnItem => returnItem.product_id === item.product_id)
          );

          // Calculate total returned quantity for this product
          let totalReturnedQuantity = 0;
          productReturns.forEach(returnSale => {
            const returnItem = returnSale.items.find(returnItem => returnItem.product_id === item.product_id);
            if (returnItem) {
              totalReturnedQuantity += parseFloat(returnItem.quantity);
            }
          });

          // Calculate net quantity
          const netQuantity = parseFloat(item.quantity) - totalReturnedQuantity;

          console.log(`Product ${item.product_id}: Original=${item.quantity}, Returned=${totalReturnedQuantity}, Net=${netQuantity}`);

          return {
            ...item,
            originalQuantity: parseFloat(item.quantity),
            returnedQuantity: totalReturnedQuantity,
            netQuantity: Math.max(0, netQuantity) // Ensure non-negative
          };
        });

        const result = {
          ...sale,
          items: itemsWithNetQuantities
        };
        return result;
      } else {
        console.error('API response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error calculating net quantities:', error);
    }

    // Return original sale if calculation fails
    console.log('Returning original sale due to error');
    return sale;
  };

  // Handle sale selection for details
  const handleSaleSelect = async (sale) => {
    console.log('Opening sale details for:', sale);
    try {
      const saleWithNetQuantities = await calculateNetQuantities(sale);
      console.log('Sale with net quantities:', saleWithNetQuantities);
      setSelectedSale(saleWithNetQuantities);
      setShowSaleDetails(true);
    } catch (error) {
      console.error('Error opening sale details:', error);
      // Fallback: show sale without net quantities calculation
      setSelectedSale(sale);
      setShowSaleDetails(true);
    }
  };

  // Handle close sale details
  const handleCloseSaleDetails = () => {
    setSelectedSale(null);
    setShowSaleDetails(false);
  };

  // Return sale function
  const handleReturnSale = () => {
    setShowReturnSaleModal(true);
    setReturnSaleSearchTerm('');
    setReturnSaleResults([]);
    setSelectedReturnSale(null);
    setReturnItems([]);
    setReturnReason('');
  };

  // Hold sales function
  const handleHoldSales = () => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty! Add items before holding sale.', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    const heldSale = {
      id: Date.now(),
      items: [...cartItems],
      totalAmount: cartItems.reduce((total, item) => total + (item.amount || 0), 0),
      timestamp: new Date().toLocaleString(),
      customerName: 'Walk-in Customer' // Default customer name
    };

    setHeldSales(prev => {
      const newHeldSales = [heldSale, ...prev];
      saveHeldSalesToStorage(newHeldSales); // Save to localStorage
      return newHeldSales;
    });
    
    // Clear cart and reset form
    setCartItems([]);
    clearCartFromStorage(); // Clear from localStorage
    setPaidAmount(0);
    setSelectedProduct(null);
    setSearchTerm('');
    setQuantity(1);
    setUomValue('Ctn');
    setSearchResults([]);
    setSelectedIndex(-1);

    // Show success message
    toast.success(`Sale held successfully! Total: ${formatCurrency(heldSale.totalAmount)}`, {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });

    // Focus back to search field
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  // Load held sale function
  const handleLoadHeldSale = (heldSale) => {
    // Check if current cart has items
    if (cartItems.length > 0) {
      toast.error('Please clear current cart before loading held sale!', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    setCartItems(heldSale.items);
    setPaidAmount(0); // Reset paid amount
    
    // Remove the held sale from the list and update localStorage
    setHeldSales(prev => {
      const updatedHeldSales = prev.filter(sale => sale.id !== heldSale.id);
      saveHeldSalesToStorage(updatedHeldSales);
      return updatedHeldSales;
    });
    
    setShowHeldSalesModal(false);

    toast.success(`Held sale loaded! ${heldSale.items.length} items restored.`, {
      duration: 3000,
      icon: '✅',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '16px',
        padding: '16px',
        borderRadius: '12px',
      },
    });

    // Focus back to search field
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  // Delete held sale function
  const handleDeleteHeldSale = (heldSaleId) => {
    showConfirmation(
      'delete-held-sale',
      heldSaleId,
      'Delete Held Sale',
      'Are you sure you want to delete this held sale? This action cannot be undone.'
    );
  };

  // Update held sales function (for POSView2)
  const updateHeldSales = (updatedHeldSalesArray) => {
    setHeldSales(updatedHeldSalesArray);
    saveHeldSalesToStorage(updatedHeldSalesArray);
    console.log('Held sales updated from POSView2:', updatedHeldSalesArray.length, 'sales');
  };

  // Handle return sale selection
  const handleReturnSaleSelect = (sale) => {
    setSelectedReturnSale(sale);
    setReturnSaleResults([]);
    setReturnSaleSearchTerm(sale.reference_number);
    setReturnItems(sale.items || []);
  };

  // Handle return item selection
  const handleReturnItemSelect = (itemId, isSelected) => {
    setReturnItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, selected: isSelected, returnQuantity: isSelected ? item.quantity : 0 }
          : item
      )
    );
  };

  // Handle return quantity change
  const handleReturnQuantityChange = (itemId, newQuantity) => {
    setReturnItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, returnQuantity: Math.min(Math.max(0, newQuantity), item.quantity) }
          : item
      )
    );
  };

  // Process return sale
  const processReturnSale = async () => {
    if (!selectedReturnSale) {
      toast.error('Please select a sale to return!', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    const selectedItems = returnItems.filter(item => item.selected && item.returnQuantity > 0);
    if (selectedItems.length === 0) {
      toast.error('Please select items to return!', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    if (!returnReason.trim()) {
      toast.error('Please provide a return reason!', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    setIsProcessingReturn(true);

    try {
      const returnData = {
        sale_id: selectedReturnSale.id,
        items: selectedItems.map(item => ({
          sale_item_id: item.id,
          product_id: item.product_id,
          quantity: item.returnQuantity,
          unit_of_measure: item.unit_of_measure,
          price: item.price
        })),
        reason: returnReason,
        total_refund_amount: selectedItems.reduce((total, item) => total + (item.price * item.returnQuantity), 0)
      };

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales/return`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(returnData)
      });

      if (response.ok) {
        const result = await response.json();
        
        toast.success(`Return processed successfully! Reference: ${result.reference_number}`, {
          duration: 5000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });

        // Close modal and reset
        setShowReturnSaleModal(false);
        setSelectedReturnSale(null);
        setReturnItems([]);
        setReturnReason('');
        setReturnSaleSearchTerm('');
        setReturnSaleResults([]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process return');
      }
    } catch (error) {
      console.error('Error processing return:', error);
      toast.error(`Failed to process return: ${error.message}`, {
        duration: 5000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    } finally {
      setIsProcessingReturn(false);
    }
  };

  // Cancel function to clear cart and reset form
  const handleCancel = () => {
    showConfirmation(
      'cancel-cart',
      null,
      'Clear Cart',
      'Are you sure you want to clear the cart? This action cannot be undone.'
    );
  };

  // Auto-print receipt after successful sale
  const printReceiptAfterSale = (saleResult, cartItems, paidAmount, paymentMethod) => {
    try {
      // Calculate totals
      const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
      const changeAmount = Math.max(0, paidAmount - totalAmount);

      // Create receipt HTML content
      const receiptContent = `
        <div style="font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.4; max-width: 300px; margin: 0 auto; padding: 10px;">
          <!-- Receipt Header -->
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 8px; letter-spacing: 2px;">${APP_NAME} SYSTEM</div>
            <div style="font-size: 10px; color: #666; margin-bottom: 8px;">Point of Sale Receipt</div>
            <div style="font-size: 10px; font-weight: bold;">${receiptSettings.company_name}</div>
            <div style="font-size: 10px; color: #666;">${receiptSettings.company_address}</div>
            <div style="font-size: 10px; color: #666;">Tel: ${receiptSettings.company_phone}</div>
            ${receiptSettings.company_email ? `<div style="font-size: 10px; color: #666;">Email: ${receiptSettings.company_email}</div>` : ''}
            ${receiptSettings.company_website ? `<div style="font-size: 10px; color: #666;">Web: ${receiptSettings.company_website}</div>` : ''}
            ${receiptSettings.tax_number ? `<div style="font-size: 10px; color: #666;">Tax ID: ${receiptSettings.tax_number}</div>` : ''}
            <div style="border-top: 1px solid #000; margin: 12px 0;"></div>
          </div>

          <!-- Receipt Details -->
          <div style="margin-bottom: 16px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Receipt #:</span>
              <span style="font-weight: bold;">${saleResult.reference_number}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Date:</span>
              <span>${new Date().toLocaleDateString('en-GB')}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Time:</span>
              <span>${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Cashier:</span>
              <span>${user?.name || user?.username || 'Unknown'}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
              <span>Payment:</span>
              <span>${paymentMethod}</span>
            </div>
            <div style="border-top: 1px solid #000;"></div>
          </div>

          <!-- Items -->
          <div style="margin-bottom: 16px;">
            <div style="margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-between; font-size: 10px; font-weight: bold;">
                <span style="width: 50%;">ITEM</span>
                <span style="width: 16.67%; text-align: center;">QTY</span>
                <span style="width: 16.67%; text-align: center;">UOM</span>
                <span style="width: 16.67%; text-align: right;">AMOUNT</span>
              </div>
              <div style="border-top: 1px solid #000; margin-top: 4px;"></div>
            </div>
            
            ${cartItems.map(item => {
              const amount = parseFloat(item.price || 0) * item.quantity;
              return `
                <div style="margin-bottom: 8px;">
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div style="width: 50%; padding-right: 8px;">
                      <div style="font-weight: bold; font-size: 10px; line-height: 1.2;">${item.product?.name || 'Unknown Product'}</div>
                      <div style="font-size: 10px; color: #666;">
                        @ ${receiptSettings.currency_symbol} ${parseFloat(item.price || 0).toFixed(2)}/${item.uom || 'PCS'}
                      </div>
                    </div>
                    <div style="width: 16.67%; text-align: center; font-size: 10px;">${item.quantity}</div>
                    <div style="width: 16.67%; text-align: center; font-size: 10px;">${item.uom || 'PCS'}</div>
                    <div style="width: 16.67%; text-align: right; font-size: 10px; font-weight: bold;">
                      ${receiptSettings.currency_symbol} ${amount.toFixed(2)}
                    </div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>

          <!-- Totals -->
          <div style="border-top: 1px solid #000; padding-top: 12px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Subtotal:</span>
              <span>${receiptSettings.currency_symbol} ${totalAmount.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px; color: #dc2626;">
              <span>Discount:</span>
              <span>-${receiptSettings.currency_symbol} 0.00</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
              <span>VAT (0%):</span>
              <span>${receiptSettings.currency_symbol} 0.00</span>
            </div>
            <div style="border-top: 1px solid #000; padding-top: 8px; margin-top: 8px;">
              <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 12px;">
                <span>TOTAL:</span>
                <span>${receiptSettings.currency_symbol} ${totalAmount.toFixed(2)}</span>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 8px; font-size: 10px;">
              <span>Amount Paid:</span>
              <span>${receiptSettings.currency_symbol} ${paidAmount.toFixed(2)}</span>
            </div>
            ${changeAmount > 0 ? `
              <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 10px; color: #16a34a;">
                <span>Change:</span>
                <span>${receiptSettings.currency_symbol} ${changeAmount.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <!-- Footer -->
          <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #000;">
            ${receiptSettings.receipt_footer.split('\n').map(line => 
              `<div style="font-size: 10px; color: #666; margin-bottom: 4px;">${line}</div>`
            ).join('')}
          </div>
        </div>
      `;

      // Create hidden iframe for printing without opening new tab
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '80mm';
      iframe.style.height = '200mm';
      iframe.style.border = 'none';
      
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <html>
          <head>
            <title>POS Receipt - ${saleResult.reference_number}</title>
            <style>
              @page {
                size: 80mm 200mm;
                margin: 0;
              }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 10px;
                background: white;
                width: 80mm;
                line-height: 1.2;
              }
              .receipt { 
                width: 100%; 
                margin: 0 auto; 
                padding: 0;
              }
              @media print {
                body { margin: 0; padding: 5px; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptContent}</div>
          </body>
        </html>
      `);
      iframeDoc.close();
      
      // Wait for content to load, then print
      iframe.onload = () => {
        iframe.contentWindow.print();
        // Clean up iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      
      // Show print notification
      toast.success('Receipt sent to printer!', {
        duration: 2000,
        icon: '🖨️',
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '14px',
          padding: '12px',
          borderRadius: '8px',
        },
      });
      
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt', {
        duration: 3000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '14px',
          padding: '12px',
          borderRadius: '8px',
        },
      });
    }
  };

  // Print receipt function - using iframe approach (same as sales history)
  const printReceipt = (receipt) => {
    try {
      // Create receipt HTML content
      const receiptContent = `
        <div style="font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.4; max-width: 300px; margin: 0 auto; padding: 10px;">
          <!-- Receipt Header -->
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="font-size: 10px; font-weight: bold;">${receipt.company_name}</div>
            <div style="font-size: 10px; color: #666;">${receipt.company_address}</div>
            <div style="font-size: 10px; color: #666;">Tel: ${receipt.company_phone}</div>
            ${receiptSettings.company_website ? `<div style="font-size: 10px; color: #666;">Web: ${receiptSettings.company_website}</div>` : ''}
            ${receiptSettings.tax_number ? `<div style="font-size: 10px; color: #666;">Tax ID: ${receiptSettings.tax_number}</div>` : ''}
            <div style="border-top: 1px solid #000; margin: 12px 0;"></div>
          </div>

          <!-- Receipt Details -->
          <div style="margin-bottom: 16px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Receipt #:</span>
              <span style="font-weight: bold;">${receipt.reference_number}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Date:</span>
              <span>${receipt.date}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Time:</span>
              <span>${receipt.time}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Cashier:</span>
              <span>${receipt.cashier_name}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
              <span>Payment:</span>
              <span>${receipt.payment_method}</span>
            </div>
            <div style="border-top: 1px solid #000;"></div>
          </div>

          <!-- Items -->
          <div style="margin-bottom: 16px;">
            <div style="margin-bottom: 12px;">
              <div style="display: flex; justify-content: space-between; font-size: 10px; font-weight: bold;">
                <span style="width: 50%;">ITEM</span>
                <span style="width: 16.67%; text-align: center;">QTY</span>
                <span style="width: 16.67%; text-align: center;">UOM</span>
                <span style="width: 16.67%; text-align: right;">AMOUNT</span>
              </div>
              <div style="border-top: 1px solid #000; margin-top: 4px;"></div>
            </div>
            
            ${receipt.items.map(item => `
              <div style="margin-bottom: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                  <div style="width: 50%; padding-right: 8px;">
                    <div style="font-weight: bold; font-size: 10px; line-height: 1.2;">${item.name}</div>
                    <div style="font-size: 10px; color: #666;">
                      @ ${receiptSettings.currency_symbol} ${parseFloat(item.price).toFixed(2)}/${item.uom}
                    </div>
                  </div>
                  <div style="width: 16.67%; text-align: center; font-size: 10px;">${item.quantity}</div>
                  <div style="width: 16.67%; text-align: center; font-size: 10px;">${item.uom}</div>
                  <div style="width: 16.67%; text-align: right; font-size: 10px; font-weight: bold;">
                    ${receiptSettings.currency_symbol} ${parseFloat(item.amount).toFixed(2)}
                  </div>
                </div>
              </div>
            `).join('')}
          </div>

          <!-- Totals -->
          <div style="border-top: 1px solid #000; padding-top: 12px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px;">
              <span>Subtotal:</span>
              <span>${receiptSettings.currency_symbol} ${parseFloat(receipt.subtotal).toFixed(2)}</span>
            </div>
            ${receipt.discount > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10px; color: #dc2626;">
                <span>Discount:</span>
                <span>-${receiptSettings.currency_symbol} ${parseFloat(receipt.discount).toFixed(2)}</span>
              </div>
            ` : ''}
            ${receipt.vat > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 10px;">
                <span>VAT (${receipt.vat_rate || 15}%):</span>
                <span>${receiptSettings.currency_symbol} ${parseFloat(receipt.vat).toFixed(2)}</span>
              </div>
            ` : ''}
            <div style="border-top: 1px solid #000; padding-top: 8px; margin-top: 8px;">
              <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 12px;">
                <span>TOTAL:</span>
                <span>${receiptSettings.currency_symbol} ${parseFloat(receipt.total_amount).toFixed(2)}</span>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 8px; font-size: 10px;">
              <span>Amount Paid:</span>
              <span>${receiptSettings.currency_symbol} ${parseFloat(receipt.amount_paid).toFixed(2)}</span>
            </div>
            ${receipt.change_amount > 0 ? `
              <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 10px; color: #16a34a;">
                <span>Change:</span>
                <span>${receiptSettings.currency_symbol} ${parseFloat(receipt.change_amount).toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <!-- Footer -->
          <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #000;">
            ${receiptSettings.receipt_footer.split('\\n').map(line => 
              `<div style="font-size: 10px; color: #666; margin-bottom: 4px;">${line}</div>`
            ).join('')}
          </div>
        </div>
      `;

      // Create hidden iframe for printing without opening new tab
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '80mm';
      iframe.style.height = '200mm';
      iframe.style.border = 'none';
      
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <html>
          <head>
            <title>POS Receipt - ${receipt.reference_number}</title>
            <style>
              @page {
                size: 80mm 200mm;
                margin: 0;
              }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 10px;
                background: white;
                width: 80mm;
                line-height: 1.2;
              }
              .receipt { 
                width: 100%; 
                margin: 0 auto; 
                padding: 0;
              }
              @media print {
                body { margin: 0; padding: 5px; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptContent}</div>
          </body>
        </html>
      `);
      iframeDoc.close();
      
      // Wait for content to load, then print
      iframe.onload = () => {
        iframe.contentWindow.print();
        // Clean up iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      
      // Show success message
      toast.success('Receipt sent to printer!', {
        duration: 2000,
        icon: '🖨️',
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '14px',
          padding: '12px',
          borderRadius: '8px',
        },
      });
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt', {
        duration: 3000,
        icon: '⚠️',
      });
    }
  };

  // Process sale function
  const processSale = async () => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty! Add items before processing sale.', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    if (paidAmount <= 0) {
      toast.error('Please enter a valid paid amount!', {
        duration: 3000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
      return;
    }

    const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
    const changeAmount = Math.max(0, paidAmount - totalAmount);

    setIsProcessingSale(true);

    try {
      const saleData = {
        items: cartItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          price: item.price,
          unit_of_measure: item.uom
        })),
        total_amount: totalAmount,
        amount_paid: paidAmount,
        payment_method: paymentMethod,
        change_amount: changeAmount
      };

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(saleData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store cart data for receipt before clearing
        const currentCartItems = [...cartItems];
        const currentPaidAmount = paidAmount;
        const currentPaymentMethod = paymentMethod;
        const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
        const changeAmount = Math.max(0, paidAmount - totalAmount);
        
        // Prepare receipt data
        const receipt = {
          reference_number: result.reference_number || `POS-${Date.now()}`,
          date: currentTime.toLocaleDateString('en-GB'),
          time: currentTime.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          cashier_name: user?.name || user?.username || 'Unknown',
          payment_method: currentPaymentMethod,
          company_name: receiptSettings.company_name || 'Grovalue Enterprise',
          company_address: receiptSettings.company_address || '123 Business Street, City, Country',
          company_phone: receiptSettings.company_phone || '+233 XX XXX XXXX',
          items: currentCartItems.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            uom: item.uom,
            price: item.price,
            amount: item.amount
          })),
          subtotal: totalAmount,
          discount: 0,
          vat: 0,
          vat_rate: 0,
          total_amount: totalAmount,
          amount_paid: currentPaidAmount,
          change_amount: changeAmount
        };
        
        // Automatically trigger print
        printReceipt(receipt);
        
        // Show success message
        toast.success(`Sale processed successfully! Reference: ${result.reference_number}`, {
          duration: 5000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: '#fff',
            fontSize: '16px',
            padding: '16px',
            borderRadius: '12px',
          },
        });

        // Clear cart and reset form
        setCartItems([]);
        clearCartFromStorage(); // Clear from localStorage
        setPaidAmount(0);
        setSelectedProduct(null);
        setSearchTerm('');
        setQuantity(1);
        setUomValue('Ctn');
        setSearchResults([]);
        setSelectedIndex(-1);

        // Focus back to search field
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);

        
        // Update transaction count after successful sale
        fetchTransactionCount();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process sale');
      }
    } catch (error) {
      console.error('Error processing sale:', error);
      toast.error(`Failed to process sale: ${error.message}`, {
        duration: 5000,
        icon: '⚠️',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    } finally {
      setIsProcessingSale(false);
    }
  };

  // F2 key binding to focus search field
  const handleF2Key = () => {
    console.log('handleF2Key called - Modal open:', modalOpen, 'Active modal:', activeModal);
    // Check if modal is open and active modal is new-sale
    if (!modalOpen || activeModal !== 'new-sale') {
      console.log('F2 pressed - Modal not open or not new-sale modal');
      return;
    }
    
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder="Search products by Name and Productl"]');
      console.log('F2 - Search input found:', !!searchInput);
      if (searchInput) {
        searchInput.focus();
        searchInput.select(); // Highlight the text for easy editing
        console.log('F2 pressed - Focus moved to Search field');
      } else {
        console.log('F2 pressed - Search input not found');
      }
    }, 100);
  };

  // F3 key binding to focus payment method dropdown
  const handleF3Key = () => {
    console.log('handleF3Key called - Modal open:', modalOpen, 'Active modal:', activeModal);
    // Check if modal is open and active modal is new-sale
    if (!modalOpen || activeModal !== 'new-sale') {
      console.log('F3 pressed - Modal not open or not new-sale modal');
      return;
    }
    
    setTimeout(() => {
      const paymentSelect = document.getElementById('payment-method-select');
      console.log('F3 - Payment select found:', !!paymentSelect);
      if (paymentSelect) {
        paymentSelect.focus();
        paymentSelect.click(); // Open the dropdown
        console.log('F3 pressed - Focus moved to Payment Method dropdown');
      } else {
        console.log('F3 pressed - Payment select not found');
      }
    }, 100);
  };

  // F4 key binding to focus paid amount field
  const handleF4Key = () => {
    console.log('handleF4Key called - Modal open:', modalOpen, 'Active modal:', activeModal);
    // Check if modal is open and active modal is new-sale
    if (!modalOpen || activeModal !== 'new-sale') {
      console.log('F4 pressed - Modal not open or not new-sale modal');
      return;
    }
    
    const totalAmount = cartItems.reduce((total, item) => total + (item.amount || 0), 0);
    setPaidAmount(totalAmount);
    
    setTimeout(() => {
      const paidAmountInput = document.getElementById('paid-amount-input');
      console.log('F4 - Paid amount input found:', !!paidAmountInput);
      if (paidAmountInput) {
        paidAmountInput.focus();
        paidAmountInput.select(); // Highlight the value for easy editing
        console.log('F4 pressed - Focus moved to Paid Amount field');
      } else {
        console.log('F4 pressed - Paid amount input not found');
      }
    }, 100);
  };

  // Auto-scroll function to keep selected item visible
  const scrollToSelectedItem = (index) => {
    const dropdown = document.querySelector('.search-results-dropdown');
    const selectedElement = dropdown?.querySelector(`[data-index="${index}"]`);
    
    if (dropdown && selectedElement) {
      const dropdownRect = dropdown.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();
      
      // Check if element is outside visible area
      if (elementRect.top < dropdownRect.top) {
        // Element is above visible area - scroll up
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (elementRect.bottom > dropdownRect.bottom) {
        // Element is below visible area - scroll down
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setSearchResults([]);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // POS Dashboard Cards
  const posCards = useMemo(() => [
    {
      id: 'new-sale',
      title: 'New Sale',
      description: 'Start a new retail transaction',
      value: formatCurrency('0'),
      change: '+0%',
      icon: ShoppingCart,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      href: '/new-sale',
      action: 'Start Sale'
    },
    {
      id: 'today-sales',
      title: 'Today\'s Sales',
      description: 'Total sales for today',
      value: formatCurrency(posStats.todaySales),
      change: '+12.5%',
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      href: '/sales-history'
    },
    {
      id: 'transactions',
      title: 'Transactions',
      description: 'Number of transactions today',
      value: posStats.todayTransactions.toString(),
      change: '+8.2%',
      icon: Receipt,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      href: '/transactions'
    },
    {
      id: 'average-ticket',
      title: 'Average Ticket',
      description: 'Average transaction value',
      value: formatCurrency(posStats.averageTicket),
      change: '+5.3%',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      href: '/analytics'
    }
  ], [posStats]);

  // Quick Actions
  const quickActions = useMemo(() => [
    {
      id: 'start-sale',
      title: 'Start New Sale',
      description: 'Begin a new retail transaction',
      icon: Plus,
      color: 'from-emerald-500 to-teal-600',
      href: '/new-sale'
    },
    {
      id: 'view-reports',
      title: 'View Reports',
      description: 'Access sales and performance reports',
      icon: TrendingUp,
      color: 'from-blue-500 to-indigo-600',
      href: '/reports'
    },
    {
      id: 'manage-returns',
      title: 'Manage Returns',
      description: 'Process customer returns and refunds',
      icon: ArrowDownRight,
      color: 'from-purple-500 to-pink-600',
      href: '/returns'
    },
    {
      id: 'inventory-check',
      title: 'Inventory Check',
      description: 'Check stock levels and availability',
      icon: Package,
      color: 'from-orange-500 to-red-600',
      href: '/inventory'
    }
  ], []);

  // Recent Transactions
  const recentTransactions = useMemo(() => [
    {
      id: 1,
      customer: 'John Doe',
      amount: 125.50,
      items: 3,
      time: '2 minutes ago',
      status: 'completed'
    },
    {
      id: 2,
      customer: 'Jane Smith',
      amount: 89.99,
      items: 2,
      time: '5 minutes ago',
      status: 'completed'
    },
    {
      id: 3,
      customer: 'Mike Johnson',
      amount: 234.75,
      items: 5,
      time: '12 minutes ago',
      status: 'completed'
    },
    {
      id: 4,
      customer: 'Sarah Wilson',
      amount: 67.25,
      items: 1,
      time: '18 minutes ago',
      status: 'completed'
    }
  ], []);




  const handleModalClose = useCallback(() => {
    console.log('🔧 Closing modal');
    setModalOpen(false);
    setActiveModal(null);
    clearModalState();
    console.log('🔧 Modal state after closing:', { isOpen: false, activeModal: null });
  }, []);

  // Simulate loading POS stats
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setPosStats({
        todaySales: 2847.50,
        todayTransactions: 23,
        averageTicket: 123.80,
        topProduct: 'Premium Coffee',
        pendingReturns: 2
      });
      setLoading(false);
    }, 1000);
  }, []);


  // Save cart items to localStorage whenever cart changes
  useEffect(() => {
    if (cartItems.length > 0) {
      saveCartToStorage(cartItems);
    } else {
      clearCartFromStorage();
    }
  }, [cartItems]);

  // Check for restored modal state on component mount
  useEffect(() => {
    const savedModalState = localStorage.getItem('posModalState');
    console.log('🔧 Checking for restored modal state:', savedModalState);
    if (savedModalState) {
      try {
        const modalState = JSON.parse(savedModalState);
        console.log('🔧 Parsed modal state:', modalState);
        if (modalState.isOpen && modalState.activeModal === 'new-sale') {
          // Ensure modal state is properly set
          console.log('🔧 Restoring modal state from localStorage');
          setModalOpen(true);
          setActiveModal('new-sale');
          
          // Show notification that modal was restored
          toast.info('POS session restored from previous session!', {
            duration: 4000,
            icon: 'ℹ️',
            style: {
              background: '#3B82F6',
              color: '#fff',
              fontSize: '16px',
              padding: '16px',
              borderRadius: '12px',
            },
          });
        }
      } catch (error) {
        console.error('🔧 Error parsing saved modal state:', error);
        // Clear corrupted localStorage data
        localStorage.removeItem('posModalState');
      }
    } else {
      console.log('🔧 No saved modal state found');
    }
  }, []);

  // Track modal state changes for debugging
  useEffect(() => {
    console.log('🔧 Modal state changed:', { modalOpen, activeModal });
  }, [modalOpen, activeModal]);

  // Handle beforeunload event to warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (modalOpen && activeModal === 'new-sale' && cartItems.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have items in your cart. Are you sure you want to leave?';
        return 'You have items in your cart. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modalOpen, activeModal, cartItems]);


  return (
    <Layout>
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2"></h1>
          <p className="text-gray-600"></p>
        </div>
      </div>



      {/* Retail POS Content */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">Retail POS Management</h1>
                  <p className="text-xl text-gray-600">Process sales transactions and manage retail operations efficiently</p>
                </div>

                {/* Four Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                  {/* Show message if user has no POS permissions */}
                  {!hasPermission('pos.sales') && !hasPermission('pos.sales_history') && !hasPermission('pos.sales_report') && !hasPermission('pos.work_period') && (
                    <div className="col-span-2">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Package size={32} className="text-yellow-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-yellow-800 mb-2">No POS Access</h3>
                        <p className="text-yellow-700">You don't have permission to access any POS features. Please contact your administrator.</p>
                      </div>
                    </div>
                  )}
                  {/* New Sale */}
                  {hasPermission('pos.sales') && (
                    <div className="bg-gradient-to-br from-white to-emerald-50/40 rounded-2xl p-8 border border-emerald-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                          <Plus size={32} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">POS Sales</h3>
                          <p className="text-gray-600">Create a new retail sale transaction</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleModalOpen('new-sale')}
                        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Open Point of Sale
                      </button>
                    </div>
                  )}

                  {/* Sales History */}
                  {hasPermission('pos.sales_history') && (
                    <div className="bg-gradient-to-br from-white to-blue-50/40 rounded-2xl p-8 border border-blue-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                          <Clock size={32} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Sales History</h3>
                          <p className="text-gray-600">View and manage all sales transactions</p>
                        </div>
                      </div>
                      <button 
                        onClick={handleSalesHistoryOpen}
                        className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        View History
                      </button>
                    </div>
                  )}

                  {/* Work Period Management */}
                  {hasPermission('pos.work_period') && (
                    <div className="bg-gradient-to-br from-white to-purple-50/40 rounded-2xl p-8 border border-purple-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg">
                          <Clock size={32} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Work Period</h3>
                          <p className="text-gray-600">Manage start and end of day operations</p>
                        </div>
                      </div>
                      <button 
                        onClick={async () => {
                          console.log('🔧 Work Period button clicked');
                          setShowWorkPeriodModal(true);
                          await fetchWorkPeriodHistory();
                        }}
                        className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Manage Work Period
                      </button>
                    </div>
                  )}

                  {/* POS Reports */}
                  {hasPermission('pos.sales_report') && (
                    <div className="bg-gradient-to-br from-white to-orange-50/40 rounded-2xl p-8 border border-orange-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                          <BarChart3 size={32} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">POS Reports</h3>
                          <p className="text-gray-600">Generate detailed POS analytics and reports</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          console.log('🔧 POS Reports button clicked');
                          handleModalOpen('pos-reports');
                        }}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Generate Reports
                      </button>
                    </div>
                  )}
                </div>
              </div>

      {/* Held Sales Modal */}
      {showHeldSalesModal && (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Clock size={24} className="text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-white">Held Sales</h2>
                  </div>
                  <button
                    onClick={() => {
                      console.log('Close button clicked');
                      setShowHeldSalesModal(false);
                    }}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {heldSales.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <Clock size={48} className="mx-auto" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">No Held Sales</h3>
                    <p className="text-gray-500">No sales are currently on hold.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {heldSales.map((heldSale) => (
                      <div key={heldSale.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">Held Sale #{heldSale.id}</h3>
                            <p className="text-sm text-gray-600">{heldSale.timestamp}</p>
                            <p className="text-sm text-gray-600">{heldSale.customerName}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-blue-600">
                              {formatCurrency(heldSale.totalAmount)}
                            </div>
                            <div className="text-sm text-gray-600">
                              {heldSale.items.length} items
                            </div>
                          </div>
                        </div>
                        
                        {/* Items List */}
                        <div className="bg-white rounded border border-gray-200 mb-3">
                          <div className="p-3 bg-gray-100 border-b border-gray-200">
                            <div className="grid grid-cols-4 gap-4 text-sm font-semibold text-gray-700">
                              <div>Product</div>
                              <div>Qty</div>
                              <div>UOM</div>
                              <div>Amount</div>
                            </div>
                          </div>
                          <div className="max-h-32 overflow-y-auto">
                            {heldSale.items.map((item, index) => (
                              <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                                <div className="grid grid-cols-4 gap-4 text-sm">
                                  <div className="font-medium text-gray-900 truncate">
                                    {item.product.name}
                                  </div>
                                  <div className="text-gray-600">{item.quantity}</div>
                                  <div className="text-gray-600">{item.uom}</div>
                                  <div className="text-gray-600">{formatCurrency(item.amount)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleLoadHeldSale(heldSale)}
                            disabled={cartItems.length > 0}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              cartItems.length > 0
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            Load
                          </button>
                          <button
                            onClick={() => handleDeleteHeldSale(heldSale.id)}
                            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Return Sale Modal */}
      {showReturnSaleModal && (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <RotateCcw size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Return Sale</h2>
                      <p className="text-red-100 text-sm">Process customer returns and refunds</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReturnSaleModal(false)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 overflow-y-auto max-h-[calc(85vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Search and Sale Details */}
                  <div className="space-y-6">
                    {/* Search Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Sale</h3>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search by sale reference number..."
                          value={returnSaleSearchTerm}
                          onChange={(e) => {
                            setReturnSaleSearchTerm(e.target.value);
                            if (e.target.value.length >= 2) {
                              searchSalesForReturn(e.target.value);
                            } else {
                              setReturnSaleResults([]);
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        
                        {/* Search Results */}
                        {returnSaleResults.length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                            {returnSaleResults.map((sale) => (
                              <div
                                key={sale.id}
                                onClick={() => handleReturnSaleSelect(sale)}
                                className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-gray-900">{sale.reference_number}</p>
                                    <p className="text-sm text-gray-600">{sale.created_at}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-gray-900">{formatCurrency(sale.total_amount)}</p>
                                    <p className="text-sm text-gray-600">{sale.items?.length || 0} items</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selected Sale Details */}
                    {selectedReturnSale ? (
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sale Details</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Reference:</span>
                            <span className="font-medium">{selectedReturnSale.reference_number}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date:</span>
                            <span className="font-medium">{selectedReturnSale.created_at}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Amount:</span>
                            <span className="font-bold text-gray-900">{formatCurrency(selectedReturnSale.total_amount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Payment Method:</span>
                            <span className="font-medium">{selectedReturnSale.payment_method}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-6 min-h-[200px] flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search size={32} className="text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Sale Selected</h3>
                          <p className="text-gray-600">Search for a sale above to view details</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Return Items and Processing */}
                  <div className="space-y-6">
                    {/* Return Items */}
                    {selectedReturnSale ? (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Items to Return</h3>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {returnItems.map((item) => (
                            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={item.selected || false}
                                    onChange={(e) => handleReturnItemSelect(item.id, e.target.checked)}
                                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                  />
                                  <div>
                                    <p className="font-medium text-gray-900">{item.product?.name || 'Product'}</p>
                                    <p className="text-sm text-gray-600">
                                      Original: {item.quantity} {item.unit_of_measure} @ {formatCurrency(item.price)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-gray-900">{formatCurrency(item.quantity * item.price)}</p>
                                </div>
                              </div>
                              
                              {item.selected && (
                                <div className="flex items-center space-x-3">
                                  <label className="text-sm text-gray-600">Return Quantity:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max={item.quantity}
                                    value={item.returnQuantity || 0}
                                    onChange={(e) => handleReturnQuantityChange(item.id, parseFloat(e.target.value) || 0)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                  <span className="text-sm text-gray-600">/ {item.quantity}</span>
                                  <span className="text-sm font-medium text-gray-900">
                                    Refund: {formatCurrency((item.returnQuantity || 0) * item.price)}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Items to Return</h3>
                        <div className="bg-gray-50 rounded-xl p-6 min-h-[256px] flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Package size={32} className="text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Items Available</h3>
                            <p className="text-gray-600">Select a sale to view items for return</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Return Reason */}
                    {selectedReturnSale ? (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Return Reason</h3>
                        <textarea
                          value={returnReason}
                          onChange={(e) => setReturnReason(e.target.value)}
                          placeholder="Enter the reason for return..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                        />
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Return Reason</h3>
                        <div className="bg-gray-50 rounded-xl p-6 min-h-[120px] flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                              <FileText size={24} className="text-gray-400" />
                            </div>
                            <p className="text-gray-600">Select a sale to enter return reason</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Return Summary */}
                    {selectedReturnSale ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Return Summary</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Items to Return:</span>
                            <span className="font-medium">
                              {returnItems.filter(item => item.selected && item.returnQuantity > 0).length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Refund Amount:</span>
                            <span className="font-bold text-red-600">
                              {formatCurrency(returnItems
                                .filter(item => item.selected && item.returnQuantity > 0)
                                .reduce((total, item) => total + (item.returnQuantity * item.price), 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Return Summary</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Items to Return:</span>
                            <span className="font-medium">0</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Refund Amount:</span>
                            <span className="font-bold text-gray-600">{formatCurrency('0.00')}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Process Return Button */}
                    {selectedReturnSale ? (
                      <button
                        onClick={processReturnSale}
                        disabled={isProcessingReturn || returnItems.filter(item => item.selected && item.returnQuantity > 0).length === 0 || !returnReason.trim()}
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed"
                      >
                        {isProcessingReturn ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Processing Return...</span>
                          </div>
                        ) : (
                          'Process Return'
                        )}
                      </button>
                    ) : (
                      <button
                        disabled={true}
                        className="w-full bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold cursor-not-allowed"
                      >
                        Select a Sale to Process Return
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"></div>
          <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
              {/* Modal Header */}
              <div className={`px-8 py-6 rounded-t-2xl ${
                confirmAction === 'exit-pos' 
                  ? 'bg-gradient-to-r from-red-500 via-red-600 to-red-700' 
                  : 'bg-gradient-to-r from-red-500 to-red-600'
              }`}>
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      {confirmAction === 'exit-pos' ? (
                        <LogOut size={32} className="text-white" />
                      ) : (
                        <X size={32} className="text-white" />
                      )}
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {confirmAction === 'cancel-cart' && 'Clear Cart'}
                      {confirmAction === 'delete-held-sale' && 'Delete Held Sale'}
                      {confirmAction === 'delete-cart-item' && 'Remove Item'}
                      {confirmAction === 'exit-pos' && 'Exit POS System'}
                    </h2>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8">
                <div className="text-center">
                  {/* Icon */}
                  <div className={`mb-6 ${
                    confirmAction === 'exit-pos' ? 'text-red-500' : 'text-red-500'
                  }`}>
                    {confirmAction === 'exit-pos' ? (
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                        <LogOut size={40} className="text-red-600" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                        <X size={40} className="text-red-600" />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {confirmAction === 'cancel-cart' && 'Clear Shopping Cart?'}
                    {confirmAction === 'delete-held-sale' && 'Delete Held Sale?'}
                    {confirmAction === 'delete-cart-item' && 'Remove Item?'}
                    {confirmAction === 'exit-pos' && 'Exit POS System?'}
                  </h3>

                  {/* Message */}
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    {confirmAction === 'cancel-cart' && 'This will clear all items from your cart. This action cannot be undone.'}
                    {confirmAction === 'delete-held-sale' && 'This will permanently delete this held sale. This action cannot be undone.'}
                    {confirmAction === 'delete-cart-item' && 'This will remove the selected item from your cart.'}
                    {confirmAction === 'exit-pos' && 'You are about to exit the POS system. Any unsaved changes or items in the cart will be lost.'}
                  </p>

                  {/* Warning for Exit POS */}
                  {confirmAction === 'exit-pos' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                          <X size={16} className="text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-red-800">Warning</p>
                          <p className="text-sm text-red-600">Unsaved changes will be lost</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-center space-x-4">
                    <button
                      onClick={() => {
                        setShowConfirmModal(false);
                        setConfirmAction(null);
                        setConfirmData(null);
                      }}
                      className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirm}
                      className={`px-8 py-3 text-white rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg ${
                        confirmAction === 'exit-pos' 
                          ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                          : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                      }`}
                    >
                      {confirmAction === 'cancel-cart' && 'Clear Cart'}
                      {confirmAction === 'delete-held-sale' && 'Delete'}
                      {confirmAction === 'delete-cart-item' && 'Remove'}
                      {confirmAction === 'exit-pos' && 'Exit POS'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal - Only for new-sale */}
      {modalOpen && activeModal === 'new-sale' && (
        <div className="fixed inset-0 z-50 overflow-hidden" style={{ top: '-40px' }}>
          {/* Full Screen Modal Content */}
          <div className="w-full h-full bg-white flex flex-col" style={{ minHeight: 'calc(100vh + 20px)' }}>
            <div className="relative w-full flex-1 overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Plus size={24} className="text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {getSetting('company_name', 'Nexpos System')}
                    </h2>
                  </div>
                  {/* Close button removed - only Exit POS button can close the modal */}
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-y-auto">
                {(() => {
                  console.log('Rendering POS view, posView =', posView);
                  return null;
                })()}
                {posView === 'view1' ? (
                  <POSView1
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleKeyDown={handleKeyDown}
                    handleSearchFocus={handleSearchFocus}
                    isSearching={isSearching}
                    searchResults={searchResults}
                    selectedIndex={selectedIndex}
                    handleProductSelect={handleProductSelect}
                    isQuantityFocused={isQuantityFocused}
                    isUomFocused={isUomFocused}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    setIsQuantityFocused={setIsQuantityFocused}
                    setIsUomFocused={setIsUomFocused}
                    uomValue={uomValue}
                    setUomValue={setUomValue}
                    selectedProduct={selectedProduct}
                    cartItems={cartItems}
                    setCartItems={setCartItems}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paidAmount={paidAmount}
                    setPaidAmount={setPaidAmount}
                    processSale={processSale}
                    isProcessingSale={isProcessingSale}
                    handleHoldSales={handleHoldSales}
                    heldSales={heldSales}
                    setShowHeldSalesModal={setShowHeldSalesModal}
                    handleCancel={handleCancel}
                    formatCurrency={formatCurrency}
                    currentTime={currentTime}
                    transactionCount={transactionCount}
                    user={user}
                    systemStatus={systemStatus}
                    showConfirmation={showConfirmation}
                    handleReturnSale={handleReturnSale}
                    addToCart={addToCart}
                  />
                ) : (
                  (() => {
                    console.log('Rendering POSView2 component');
                    return <POSView2
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleKeyDown={handleKeyDown}
                    handleSearchFocus={handleSearchFocus}
                    isSearching={isSearching}
                    searchResults={searchResults}
                    selectedIndex={selectedIndex}
                    handleProductSelect={handleProductSelect}
                    isQuantityFocused={isQuantityFocused}
                    isUomFocused={isUomFocused}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    setIsQuantityFocused={setIsQuantityFocused}
                    setIsUomFocused={setIsUomFocused}
                    uomValue={uomValue}
                    setUomValue={setUomValue}
                    selectedProduct={selectedProduct}
                    cartItems={cartItems}
                    setCartItems={setCartItems}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paidAmount={paidAmount}
                    setPaidAmount={setPaidAmount}
                    processSale={processSale}
                    isProcessingSale={isProcessingSale}
                    handleHoldSales={handleHoldSales}
                    heldSales={heldSales}
                    updateHeldSales={updateHeldSales}
                    handleLoadHeldSale={handleLoadHeldSale}
                    setShowHeldSalesModal={setShowHeldSalesModal}
                    handleCancel={handleCancel}
                    formatCurrency={formatCurrency}
                    currentTime={currentTime}
                    transactionCount={transactionCount}
                    user={user}
                    systemStatus={systemStatus}
                    showConfirmation={showConfirmation}
                    handleReturnSale={handleReturnSale}
                    addToCart={addToCart}
                  />;
                  })()
                )}
                                </div>
                            </div>

            {/* Modal Footer - Outside of scrollable body */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white px-4 lg:px-10 py-3 lg:py-4 border-t-2 border-white/20 flex-shrink-0 shadow-lg">
                      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-2 lg:space-y-0">
                        <div className="flex flex-wrap items-center gap-3 lg:gap-6 text-xs lg:text-sm">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${systemStatus.online ? 'bg-green-400 animate-pulse' : 'bg-red-500 animate-bounce'}`}></div>
                            <span className={`font-bold ${systemStatus.online ? 'text-white' : 'text-red-200'}`}>
                              {systemStatus.online ? 'SYSTEM ONLINE' : 'SYSTEM OFFLINE'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white/80">Cashier:</span>
                            <span className="font-bold text-white">{user?.name || user?.username || 'Unknown'}</span>
                        </div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white/80">Session:</span>
                            <span className={`font-bold ${systemStatus.active ? 'text-green-300' : 'text-red-300'}`}>
                              {systemStatus.active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                      </div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white/80">Items in Cart:</span>
                            <span className="font-bold text-white text-lg">{cartItems.length}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 lg:gap-4 text-xs lg:text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white/80">Transactions:</span>
                            <span className="font-bold text-white text-lg">{transactionCount}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white/80">Time:</span>
                            <span className="font-bold text-white text-lg">
                              {currentTime.toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                          </div>
                        </div>
      )}

      {/* Separate Smaller Modals for Work Period, POS Reports, and Sales History */}
      {(activeModal === 'work-period' || activeModal === 'pos-reports' || activeModal === 'sales-history') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`bg-white rounded-lg shadow-xl w-full ${
            activeModal === 'sales-history' 
              ? 'max-w-6xl max-h-[85vh] flex flex-col' 
              : 'max-w-2xl max-h-[80vh] overflow-y-auto'
          }`}>
            {/* Modal Header */}
                          <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                  {/* Left Side - Title */}
                  <div className="flex items-center space-x-3">
                    {activeModal === 'work-period' && <Clock size={24} className="text-white" />}
                    {activeModal === 'pos-reports' && <BarChart3 size={24} className="text-white" />}
                    {activeModal === 'sales-history' && <Clock size={24} className="text-white" />}
                    <h2 className="text-xl font-bold text-white">
                      {activeModal === 'work-period' && 'Work Period Management'}
                      {activeModal === 'pos-reports' && 'POS Reports'}
                      {activeModal === 'sales-history' && 'Sales History'}
                    </h2>
                          </div>

                  {/* Center - Sales Value (only for sales-history) */}
                {activeModal === 'sales-history' && (
                    <div className="flex-1 flex justify-center">
                      <div className="text-white text-lg font-semibold">
                        Sales Value: {formatCurrency(salesHistory.reduce((sum, sale) => sum + (sale.netTotalAmount || parseFloat(sale.total_amount || 0)), 0))}
                        </div>
                      </div>
                  )}

                  {/* Right Side - Close Button (only for sales-history) */}
                  {activeModal === 'sales-history' && (
                    <div className="flex items-center">
                      <button
                        onClick={handleModalClose}
                        className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors shadow-sm font-medium text-sm"
                      >
                        Close
                        </button>
                      </div>
                )}
                    </div>
              </div>

            {/* Modal Body */}
            <div className={`${activeModal === 'sales-history' ? 'flex-1 flex flex-col' : 'p-6'}`}>

                             {activeModal === 'work-period' && (
                 <div className="flex flex-col h-full p-6">
                   {/* Current Work Period Status */}
                   <div className="bg-gray-50 p-6 rounded-lg mb-6">
                     <div className="flex items-center justify-between">
                          <div>
                         <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Work Period Status</h3>
                         <div className="flex items-center space-x-4">
                           <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                             workPeriod.isActive 
                               ? 'bg-green-100 text-green-800' 
                               : 'bg-red-100 text-red-800'
                           }`}>
                             {workPeriod.isActive ? '🟢 ACTIVE' : '🔴 INACTIVE'}
                          </div>
                           {workPeriod.isActive && (
                             <div className="text-sm text-gray-600">
                               Started: {workPeriod.startDate} at {workPeriod.startTime}
                        </div>
                           )}
                          </div>
                        </div>
                       <div className="text-right">
                         <div className="text-2xl font-bold text-purple-600">
                           {formatCurrency(workPeriod.totalSales)}
                      </div>
                         <div className="text-sm text-gray-600">
                           Total Sales: {workPeriod.totalTransactions} transactions
                    </div>
                          </div>
                        </div>
                      </div>

                   {/* Work Period Actions */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                     <div className="bg-white border border-gray-200 rounded-lg p-6">
                       <h4 className="text-lg font-semibold text-gray-900 mb-4">Start Work Period</h4>
                       <p className="text-gray-600 mb-4">Begin a new work day to enable POS operations</p>
                       <button 
                         onClick={startWorkPeriod}
                         disabled={workPeriod.isActive}
                         className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                           workPeriod.isActive
                             ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                             : 'bg-green-500 hover:bg-green-600 text-white'
                         }`}
                       >
                         {workPeriod.isActive ? 'Already Active' : 'Start Work Period'}
                      </button>
                    </div>
                     
                     <div className="bg-white border border-gray-200 rounded-lg p-6">
                       <h4 className="text-lg font-semibold text-gray-900 mb-4">End Work Period</h4>
                       <p className="text-gray-600 mb-4">Close the current work day and disable POS</p>
                       <button 
                         onClick={endWorkPeriod}
                         disabled={!workPeriod.isActive}
                         className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                           !workPeriod.isActive
                             ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                             : 'bg-red-500 hover:bg-red-600 text-white'
                         }`}
                       >
                         {!workPeriod.isActive ? 'No Active Period' : 'End Work Period'}
                      </button>
                  </div>
                   </div>

                   {/* Work Period History */}
                   <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                     <div className="bg-slate-500 px-6 py-3">
                       <h4 className="text-lg font-semibold text-white">Work Period History</h4>
                      </div>
                     <div className="p-6">
                       <div className="space-y-4">
                         <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                             <div className="font-medium text-gray-900">Today's Work Period</div>
                             <div className="text-sm text-gray-600">
                               {workPeriod.startDate} - {workPeriod.startTime}
                               {workPeriod.endTime && ` to ${workPeriod.endTime}`}
                      </div>
                    </div>
                           <div className="text-right">
                             <div className="font-medium text-gray-900">{formatCurrency(workPeriod.totalSales)}</div>
                             <div className="text-sm text-gray-600">{workPeriod.totalTransactions} transactions</div>
                        </div>
                          </div>
                          </div>
                          </div>
                        </div>
                      </div>
                )}

                                {activeModal === 'pos-reports' && (
                  <div className="flex flex-col h-full">
                    {/* Fixed Header Section */}
                    <div className="flex-shrink-0 space-y-6">
                      {/* Date Range Filter */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex flex-col sm:flex-row gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.startDate}
                              onChange={(e) => handleReportsDateChange('startDate', e.target.value)}
                            />
                    </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.endDate}
                              onChange={(e) => handleReportsDateChange('endDate', e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={handleApplyReportsFilter}
                              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Apply Filter
                      </button>
                            <button 
                              onClick={handleTodayReportsFilter}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Today
                      </button>
                    </div>
                  </div>
                      </div>

                      {/* Report Tabs */}
                      <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setActiveReportTab('dashboard')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'dashboard'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={() => setActiveReportTab('sales')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'sales'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Sales Report
                          </button>
                          <button
                            onClick={() => setActiveReportTab('products')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'products'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Products
                          </button>
                          <button
                            onClick={() => setActiveReportTab('payments')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'payments'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Payment Methods
                          </button>
                        </nav>
                      </div>
                    </div>

                    {/* Scrollable Content Section */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {/* Loading State */}
                      {reportsLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                          <span className="ml-2 text-gray-600">Loading reports...</span>
                  </div>
                )}

                    {/* Dashboard Tab */}
                    {activeReportTab === 'dashboard' && !reportsLoading && reportsData.dashboardStats && (
                  <div className="space-y-6">
                        {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                        <h3 className="font-medium text-emerald-900">Today's Sales</h3>
                            <p className="text-2xl font-bold text-emerald-600">
                              {formatCurrency(parseFloat(reportsData.dashboardStats.today?.sales || 0))}
                            </p>
                            <p className="text-sm text-emerald-600">
                              {reportsData.dashboardStats.today?.transactions || 0} transactions
                            </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h3 className="font-medium text-blue-900">This Month</h3>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatCurrency(parseFloat(reportsData.dashboardStats.month?.sales || 0))}
                            </p>
                            <p className="text-sm text-blue-600">
                              {reportsData.dashboardStats.month?.transactions || 0} transactions
                            </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <h3 className="font-medium text-orange-900">This Year</h3>
                            <p className="text-2xl font-bold text-orange-600">
                              {formatCurrency(parseFloat(reportsData.dashboardStats.year?.sales || 0))}
                            </p>
                            <p className="text-sm text-orange-600">
                              {reportsData.dashboardStats.year?.transactions || 0} transactions
                            </p>
                      </div>
                    </div>

                        {/* Recent Sales */}
                        <div className="bg-white border border-gray-200 rounded-lg">
                          <div className="px-4 py-3 border-b border-gray-200">
                            <h3 className="font-medium text-gray-900">Recent Sales</h3>
                        </div>
                          <div className="divide-y divide-gray-200">
                            {reportsData.dashboardStats.recent_sales?.map((sale, index) => (
                              <div key={index} className="px-4 py-3">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {sale.reference_number}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {sale.user?.name || 'Unknown'} • {new Date(sale.created_at).toLocaleString()}
                                    </p>
                        </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">
                                      {formatCurrency(parseFloat(sale.total_amount))}
                                    </p>
                                    <p className="text-sm text-gray-500">{sale.payment_method}</p>
                      </div>
                    </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sales Report Tab */}
                    {activeReportTab === 'sales' && !reportsLoading && reportsData.salesReport && (
                      <div className="space-y-6">
                        {/* Sales Summary */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h3 className="font-medium text-gray-900 mb-3">Sales Summary</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-emerald-600">
                                {formatCurrency(parseFloat(reportsData.salesReport.summary?.total_sales || 0))}
                              </p>
                              <p className="text-sm text-gray-600">Total Sales</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {reportsData.salesReport.summary?.total_transactions || 0}
                              </p>
                              <p className="text-sm text-gray-600">Total Transactions</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-orange-600">
                                {formatCurrency(parseFloat(reportsData.salesReport.summary?.average_sale || 0))}
                              </p>
                              <p className="text-sm text-gray-600">Average Sale</p>
                            </div>
                          </div>
                        </div>

                        {/* Sales Table */}
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200">
                            <h3 className="font-medium text-gray-900">Daily Sales Breakdown</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Transactions
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Sales
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Average Sale
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {reportsData.salesReport.sales?.map((sale, index) => (
                                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {sale.date || sale.week_start || sale.month_start}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {sale.total_transactions}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {formatCurrency(parseFloat(sale.total_sales || 0))}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {formatCurrency(parseFloat(sale.average_sale || 0))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Products Tab */}
                    {activeReportTab === 'products' && !reportsLoading && reportsData.productReport && (
                      <div className="space-y-6">
                        {/* Products Summary */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h3 className="font-medium text-gray-900 mb-3">Products Summary</h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-emerald-600">
                                {reportsData.productReport.summary?.total_products_sold || 0}
                              </p>
                              <p className="text-sm text-gray-600">Products Sold</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {reportsData.productReport.summary?.total_quantity_sold || 0}
                              </p>
                              <p className="text-sm text-gray-600">Total Quantity</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-orange-600">
                                {formatCurrency(parseFloat(reportsData.productReport.summary?.total_revenue || 0))}
                              </p>
                              <p className="text-sm text-gray-600">Total Revenue</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-red-600">
                                {reportsData.productReport.summary?.low_stock_count || 0}
                              </p>
                              <p className="text-sm text-gray-600">Low Stock Items</p>
                            </div>
                          </div>
                        </div>

                        {/* Top Products */}
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200">
                            <h3 className="font-medium text-gray-900">Top Selling Products</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Product
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity Sold
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Revenue
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Avg Price
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Transactions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {reportsData.productReport.top_products?.map((product, index) => (
                                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {product.name}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {product.total_quantity_sold}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {formatCurrency(parseFloat(product.total_revenue || 0))}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {formatCurrency(parseFloat(product.average_price || 0))}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {product.transaction_count}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Low Stock Products */}
                        {reportsData.productReport.low_stock_products?.length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Low Stock Products</h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Product
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Current Stock
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Pieces per CTN
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {reportsData.productReport.low_stock_products?.map((product, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {product.name}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                          product.quantity <= 5 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {product.quantity} pieces
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {product.pieces_in_ctn}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Payment Methods Tab */}
                    {activeReportTab === 'payments' && !reportsLoading && reportsData.paymentMethodReport && (
                      <div className="space-y-6">
                        {/* Payment Summary */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h3 className="font-medium text-gray-900 mb-3">Payment Methods Summary</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-emerald-600">
                                {reportsData.paymentMethodReport.summary?.total_transactions || 0}
                              </p>
                              <p className="text-sm text-gray-600">Total Transactions</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {formatCurrency(parseFloat(reportsData.paymentMethodReport.summary?.total_amount || 0))}
                              </p>
                              <p className="text-sm text-gray-600">Total Amount</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-orange-600">
                                {reportsData.paymentMethodReport.summary?.payment_methods_count || 0}
                              </p>
                              <p className="text-sm text-gray-600">Payment Methods</p>
                            </div>
                          </div>
                        </div>

                        {/* Payment Methods Table */}
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200">
                            <h3 className="font-medium text-gray-900">Payment Methods Breakdown</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Method
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Transactions
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Amount
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Average Amount
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Percentage
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {reportsData.paymentMethodReport.payment_methods?.map((method, index) => {
                                  const percentage = reportsData.paymentMethodReport.summary?.total_amount > 0 
                                    ? (method.total_amount / reportsData.paymentMethodReport.summary.total_amount) * 100 
                                    : 0;
                                  
                                  return (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {method.payment_method}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {method.transaction_count}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {formatCurrency(parseFloat(method.total_amount || 0))}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {formatCurrency(parseFloat(method.average_amount || 0))}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                          {percentage.toFixed(1)}%
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3">
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Export PDF
                      </button>
                      <button onClick={handleModalClose} className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300">
                        Close
                      </button>
                    </div>
                    </div>
                  </div>
                )}

                            {activeModal === 'sales-history' && (
                <div className="flex flex-col h-full p-6">
                  {/* Fixed Header Section */}
                  <div className="flex-shrink-0">
                    {/* Header with Sales Value and Filters */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex flex-col lg:flex-row justify-center items-start lg:items-center gap-4">

                        {/* Right Side - Filters */}
                        <div className="flex flex-col sm:flex-row gap-3 items-end">
                          <div className="flex flex-col sm:flex-row gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                              <input 
                                type="date" 
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                value={salesHistoryStartDate || ''}
                                onChange={(e) => setSalesHistoryStartDate(e.target.value)}
                              />
              </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                              <input 
                                type="date" 
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                value={salesHistoryEndDate || ''}
                                onChange={(e) => setSalesHistoryEndDate(e.target.value)}
                              />
            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                              <select 
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                value={salesHistoryStatus || 'all'}
                                onChange={(e) => setSalesHistoryStatus(e.target.value)}
                              >
                                <option value="all">All</option>
                                <option value="completed">Completed</option>
                                <option value="return">Return</option>
                              </select>
          </div>
        </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={handleApplySalesHistoryFilter}
                              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-sm"
                            >
                              Apply Filter
                            </button>
                            <button 
                              onClick={handleTodayFilter}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors text-sm"
                            >
                              Today
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table Container with Controlled Height */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" style={{ height: '400px' }}>
                    {/* Single Table with Fixed Header and Scrollable Body */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-500 font-semibold text-white-600 sticky top-0">
                          <tr>
                            <th 
                              className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors"
                              onClick={() => handleSort('created_at')}
                            >
              <div className="flex items-center justify-between">
                                <span>DATE</span>
                                <div className="flex flex-col ml-1">
                                  <ChevronUp size={12} className={`${salesHistorySortBy === 'created_at' && salesHistorySortOrder === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} />
                                  <ChevronDown size={12} className={`${salesHistorySortBy === 'created_at' && salesHistorySortOrder === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                </div>
                            </th>
                            <th 
                              className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors"
                              onClick={() => handleSort('reference_number')}
                            >
                              <div className="flex items-center justify-between">
                                <span>INVOICE NUMBER</span>
                                <div className="flex flex-col ml-1">
                                  <ChevronUp size={12} className={`${salesHistorySortBy === 'reference_number' && salesHistorySortOrder === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} />
                                  <ChevronDown size={12} className={`${salesHistorySortBy === 'reference_number' && salesHistorySortOrder === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} />
                                </div>
                              </div>
                            </th>
                            <th 
                              className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors"
                              onClick={() => handleSort('total_amount')}
                            >
                              <div className="flex items-center justify-between">
                                <span>AMOUNT</span>
                                <div className="flex flex-col ml-1">
                                  <ChevronUp size={12} className={`${salesHistorySortBy === 'total_amount' && salesHistorySortOrder === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} />
                                  <ChevronDown size={12} className={`${salesHistorySortBy === 'total_amount' && salesHistorySortOrder === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} />
                                </div>
                              </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">PROCESSED BY</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ACTION</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {salesHistoryLoading ? (
                            <tr>
                              <td colSpan="5" className="px-4 py-8">
                                <div className="flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                  <span className="ml-2 text-gray-600">Loading sales history...</span>
                                </div>
                              </td>
                            </tr>
                          ) : salesHistory.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-4 py-8">
                                <div className="text-center">
                                  <Clock size={48} className="mx-auto text-gray-400 mb-4" />
                                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Sales Found</h3>
                                  <p className="text-gray-600">No sales match your current search criteria.</p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            salesHistory.map((sale, index) => (
                              <tr key={sale.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {new Date(sale.created_at).toLocaleDateString('en-GB').replace(/\//g, '-')} {new Date(sale.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{sale.reference_number}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatCurrency(sale.netTotalAmount || parseFloat(sale.total_amount || 0))}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{user?.name || user?.username || 'Unknown'}</div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                <button
                                    onClick={() => handleSaleSelect(sale)}
                                    className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded font-medium transition-colors"
                >
                                    View
                </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
              </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer with Pagination (only for sales-history) */}
              {activeModal === 'sales-history' && (
              <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4 rounded-b-lg">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((salesHistoryCurrentPage - 1) * salesHistoryPerPage) + 1} to {Math.min(salesHistoryCurrentPage * salesHistoryPerPage, salesHistoryTotal)} of {salesHistoryTotal} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={handlePreviousPage}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={salesHistoryCurrentPage <= 1}
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm bg-blue-500 text-white rounded">{salesHistoryCurrentPage}</span>
                    <button 
                      onClick={handleNextPage}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={salesHistoryCurrentPage >= Math.ceil(salesHistoryTotal / salesHistoryPerPage)}
                    >
                      Next
                      </button>
                    </div>
                  </div>
                        </div>
            )}
                      </div>
                        </div>
      )}

      {/* Sale Details Modal */}
      {showSaleDetails && selectedSale && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Receipt size={24} className="text-white" />
                  <h2 className="text-xl font-bold text-white">Sale Details</h2>
                      </div>
                <button
                  onClick={handleCloseSaleDetails}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                    </button>
                  </div>
                </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Sale Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Sale Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Invoice Number:</span>
                      <span className="font-medium">{selectedSale?.reference_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium">
                        {selectedSale?.created_at ? new Date(selectedSale.created_at).toLocaleDateString('en-GB') : 'N/A'} {selectedSale?.created_at ? new Date(selectedSale.created_at).toLocaleTimeString('en-GB') : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="font-medium">{selectedSale?.payment_method || 'N/A'}</span>
                  </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium ${selectedSale?.is_return ? 'text-red-600' : 'text-green-600'}`}>
                        {selectedSale?.is_return ? 'Return' : 'Completed'}
                      </span>
                      </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Summary</h3>
                      <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="font-bold text-lg">
                        {formatCurrency(selectedSale?.items ? 
                          selectedSale.items.reduce((total, item) => {
                            const quantity = item.netQuantity !== undefined ? item.netQuantity : item.quantity;
                            return total + (parseFloat(item.price || 0) * quantity);
                          }, 0) : 
                          parseFloat(selectedSale?.total_amount || 0)
                        )}
                      </span>
                    </div>
                        <div className="flex justify-between">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-medium">
                        {formatCurrency(selectedSale?.items ? 
                          selectedSale.items.reduce((total, item) => {
                            const quantity = item.netQuantity !== undefined ? item.netQuantity : item.quantity;
                            return total + (parseFloat(item.price || 0) * quantity);
                          }, 0) : 
                          parseFloat(selectedSale?.amount_paid || 0)
                        )}
                      </span>
                        </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Change:</span>
                      <span className="font-medium">{formatCurrency(parseFloat(selectedSale?.change_amount || 0))}</span>
                        </div>
                    {selectedSale?.is_return && selectedSale?.return_reason && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Return Reason:</span>
                        <span className="font-medium text-red-600">{selectedSale.return_reason}</span>
                      </div>
                    )}
                    </div>
                  </div>
              </div>

              {/* Sale Items */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Quantity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Unit Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedSale?.items && selectedSale.items.length > 0 ? (
                        selectedSale.items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{item.product?.name || 'Unknown Product'}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {item.netQuantity !== undefined ? item.netQuantity : item.quantity} {item.unit_of_measure}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatCurrency(parseFloat(item.price || 0))}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(parseFloat(item.price * (item.netQuantity !== undefined ? item.netQuantity : item.quantity) || 0))}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                            No items found for this sale
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handlePrintReceiptFromHistory}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                >
                  Print Receipt
                    </button>
                <button
                  onClick={handleCloseSaleDetails}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                      Close
                    </button>
              </div>
            </div>
                  </div>
                </div>
              )}

      {/* Work Period Modal */}
      {showWorkPeriodModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock size={24} className="text-white" />
                  <h2 className="text-xl font-bold text-white">Work Period Management</h2>
                    </div>
                <button
                  onClick={() => setShowWorkPeriodModal(false)}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
                    </div>
                    </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Current Work Period Status */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Work Period Status</h3>
                    <div className="flex items-center space-x-4">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        workPeriod.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {workPeriod.isActive ? '🟢 ACTIVE' : '🔴 INACTIVE'}
                  </div>
                      {workPeriod.isActive && (
                        <div className="text-sm text-gray-600">
                          Started: {workPeriod.startDate} at {workPeriod.startTime}
                      </div>
                      )}
                      </div>
                    </div>

                  </div>
              </div>

              {/* Work Period Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Start Work Period</h4>
                  <p className="text-gray-600 mb-4">Begin a new work day to enable POS operations</p>
                  <button 
                    onClick={startWorkPeriod}
                    disabled={workPeriod.isActive}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      workPeriod.isActive
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                  >
                    {workPeriod.isActive ? 'Already Active' : 'Start Work Period'}
                    </button>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">End Work Period</h4>
                  <p className="text-gray-600 mb-4">Close the current work day and disable POS</p>
                  <button 
                    onClick={endWorkPeriod}
                    disabled={!workPeriod.isActive}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      !workPeriod.isActive
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                  >
                    {!workPeriod.isActive ? 'No Active Period' : 'End Work Period'}
                    </button>
                  </div>
              </div>

                                 {/* Work Period History */}
                   <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                     <div className="bg-slate-500 px-6 py-3">
                       <h4 className="text-lg font-semibold text-white">Work Period History</h4>
                     </div>
                     <div className="p-6">
                       {workPeriodLoading ? (
                         <div className="flex items-center justify-center py-8">
                           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                           <span className="ml-2 text-gray-600">Loading history...</span>
                         </div>
                       ) : workPeriodHistory.length > 0 ? (
                         <div className="space-y-4 max-h-64 overflow-y-auto">
                           {workPeriodHistory.map((period) => (
                             <div key={period.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                               <div>
                                 <div className="font-medium text-gray-900">
                                   {new Date(period.work_date).toLocaleDateString('en-GB')}
                                 </div>
                                 <div className="text-sm text-gray-600">
                                   {period.start_time} - {period.end_time || 'Active'}
                                 </div>
                                 <div className="text-xs text-gray-500">
                                   Status: {period.status}
                                 </div>
                               </div>
                               <div className="text-right">
                                 <div className="text-sm text-gray-600">
                                   Started by: {period.user?.name || period.user?.username || 'Unknown'}
                                 </div>
                                 {period.total_sales > 0 && (
                                   <div className="text-sm font-medium text-purple-600">
                                     {formatCurrency(parseFloat(period.total_sales))}
                </div>
              )}
                                 {period.total_transactions > 0 && (
                                   <div className="text-xs text-gray-500">
                                     {period.total_transactions} transactions
            </div>
                                 )}
                               </div>
                             </div>
                           ))}
                         </div>
                       ) : (
                         <div className="text-center py-8">
                           <div className="text-gray-400 mb-2">
                             <Clock size={32} className="mx-auto" />
                           </div>
                           <p className="text-gray-600">No work period history found</p>
                         </div>
                       )}
                     </div>
                   </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceipt && (
        <POSReceipt
          receiptData={receiptData}
          onClose={() => setShowReceipt(false)}
          onPrint={handleReceiptPrint}
          onDownload={handleReceiptDownload}
          formatCurrency={formatCurrency}
          receiptSettings={receiptSettings}
        />
      )}
    </Layout>
  );
};

export default RetailPOS;
